import React, { useState, useEffect, useRef } from 'react';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import useTxtLearningFlow from '../../hooks/useTxtLearningFlow';
import { previewTxtTemplate, saveTxtSelections, uploadTxtResults } from '../../services/denemeTxtService';

// --- DİNAMİK SINAV VE DERS YAPISI ---
const EXAM_SUBJECTS = {
  TYT: ['Turkce', 'Sosyal Bilgiler', 'Matematik', 'Fen Bilimleri'],
  AYT: ['Turk Dili ve Edebiyati', 'Sosyal Bilimler-1', 'Sosyal Bilimler-2', 'Matematik', 'Fen Bilimleri'],
  LGS: ['Turkce', 'Matematik', 'Fen Bilimleri', 'Inkilap Tarihi', 'Din Kulturu', 'Yabanci Dil']
};
const KARNE_CORE_DERSLER = ['Türkçe', 'Sosyal Bilgiler', 'Matematik', 'Fen Bilimleri'];

const EXAM_SUBJECT_QUESTION_COUNTS = {
  TYT: { turkce: 40, sosyal: 20, matematik: 40, fen: 20 },
  AYT: { turk: 24, sosyal1: 40, sosyal2: 40, matematik: 40, fen: 40 },
  LGS: { turkce: 20, matematik: 20, fen: 20, inkilap: 10, din: 10, yabanci: 10 },
};

const TXT_TEMPLATE_BASE_FIELDS = [
  'AdSoyad',
  'OgrenciNo',
  'TcKimlik',
  'Kitapcik',
];

const TXT_TEMPLATE_FIELD_LABELS = {
  AdSoyad: 'Ad Soyad',
  OgrenciNo: 'Okul Numarası',
  TcKimlik: 'TC Kimlik',
  Kitapcik: 'Kitapçık',
};

const TXT_TEMPLATE_FIELD_HINTS = {
  AdSoyad: 'Satırdan ad soyad kısmını seçin',
  OgrenciNo: 'Okul numarasını seçin',
  TcKimlik: '11 haneli TC kısmını seçin',
  Kitapcik: 'Kitapçık harfini seçin',
};

const getTxtTemplateFieldLabel = (field) => TXT_TEMPLATE_FIELD_LABELS[field] || field;
const getTxtTemplateFieldHint = (field) => TXT_TEMPLATE_FIELD_HINTS[field] || `${field} cevaplarını satırdan seçin`;
const normalizeAnswerChars = (value = '') => {
  // Optik TXT'deki boşlukları (boş soruları) silmek yerine yıldıza (*) çevir ki harf kayması olmasın!
  return String(value || '')
    .toUpperCase()
    .replace(/\s+/g, '*') // Önce tüm boşlukları yıldıza çevir
    .replace(/[^ABCDE*X]/g, ''); // Sonra geçersiz karakterleri temizle
};
const normalizeLessonToken = (value = '') => {
  const raw = String(value || '')
    .toLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');

  // Ders adlarındaki farklı yazımları tek anahtar altında toplar.
  if (raw.includes('sosyalbilimler1') || raw.includes('sosyal1')) return 'sosyalbilimler1';
  if (raw.includes('sosyalbilimler2') || raw.includes('sosyal2')) return 'sosyalbilimler2';
  if (raw.includes('sosyalbilgiler') || raw === 'sosyalb' || raw.startsWith('sosyalb') || raw === 'sosyal') return 'sosyalbilgiler';
  if (raw.includes('turk')) return 'turkce';
  if (raw.includes('mat')) return 'matematik';
  if (raw.includes('fen')) return 'fenbilimleri';
  if (raw.includes('inkilap')) return 'inkilaptarihi';
  if (raw.includes('din')) return 'dinkulturu';
  if (raw.includes('yabanci') || raw.includes('ingilizce') || raw.includes('dil')) return 'yabancidil';

  return raw;
};

const getEstimatedQuestionCount = (examType, lessonName) => {
  const lesson = normalizeLessonToken(lessonName);
  const counts = EXAM_SUBJECT_QUESTION_COUNTS[examType] || {};
  if (lesson.includes('turk')) return counts.turkce || counts.turk || 0;
  if (lesson.includes('sosyalbilimler1') || lesson.includes('sosyal1')) return counts.sosyal1 || 0;
  if (lesson.includes('sosyalbilimler2') || lesson.includes('sosyal2')) return counts.sosyal2 || 0;
  if (lesson.includes('sosyal')) return counts.sosyal || 0;
  if (lesson.includes('mat')) return counts.matematik || 0;
  if (lesson.includes('fen')) return counts.fen || 0;
  if (lesson.includes('inkilap')) return counts.inkilap || 0;
  if (lesson.includes('din')) return counts.din || 0;
  if (lesson.includes('yabanci') || lesson.includes('ingilizce') || lesson.includes('dil')) return counts.yabanci || 0;
  return 0;
};

const normalizeDersAdi = (value = '') => {
  const normalized = normalizeLessonToken(value);
  if (normalized.includes('turk')) return 'Türkçe';
  if (normalized.includes('sosyalbilimler1') || normalized.includes('sosyal1')) return 'Sosyal Bilimler-1';
  if (normalized.includes('sosyalbilimler2') || normalized.includes('sosyal2')) return 'Sosyal Bilimler-2';
  if (normalized.includes('sosyal')) return 'Sosyal Bilgiler';
  if (normalized.includes('mat')) return 'Matematik';
  if (normalized.includes('fen')) return 'Fen Bilimleri';
  if (normalized.includes('inkilap')) return 'İnkılap Tarihi';
  if (normalized.includes('din')) return 'Din Kültürü';
  if (normalized.includes('yabanci') || normalized.includes('ingilizce') || normalized.includes('dil')) return 'Yabancı Dil';
  return String(value || '').trim();
};

const canonicalLessonLabel = (value = '') => normalizeDersAdi(String(value || '').trim());

const uniqLessonLabels = (list = []) => {
  const map = new Map();
  (list || []).forEach((item) => {
    const label = canonicalLessonLabel(item);
    const key = normalizeLessonToken(label);
    if (!key) return;
    if (!map.has(key)) map.set(key, label);
  });
  return Array.from(map.values());
};

const tryFindStudentLessonAnswers = (student = {}, ders) => {
  const normalized = normalizeLessonToken(ders);
  const candidates = [
    student?.dersCevaplari,
    student?.cevaplar,
    student?.answers,
    student?.lessonAnswers,
    student?.ders_cevaplari,
  ];
  for (const source of candidates) {
    if (!source || typeof source !== 'object') continue;
    const entries = Object.entries(source);
    const direct = entries.find(([key]) => normalizeLessonToken(key) === normalized);
    if (direct) return normalizeAnswerChars(direct[1]);
  }
  return '';
};

const splitName = (fullName = '') => {
  const clean = String(fullName || '').trim();
  if (!clean) return { ad: '', soyad: '' };
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { ad: parts[0], soyad: '' };
  return { ad: parts.slice(0, -1).join(' '), soyad: parts.slice(-1)[0] };
};

const normalizeDetayItem = (item = {}, fallbackDers = '') => {
  const ders = item?.ders || item?.ders_adi || item?.lesson || item?.brans || item?.branch || fallbackDers;
  const soru = Number(
    item?.soru ??
    item?.soru_sayisi ??
    item?.question_count ??
    item?.total_question ??
    item?.q ??
    0
  );
  const dogru = Number(item?.dogru ?? item?.correct ?? item?.d ?? 0);
  const yanlis = Number(item?.yanlis ?? item?.wrong ?? item?.incorrect ?? item?.y ?? 0);
  const bos = Number(item?.bos ?? item?.empty ?? item?.b ?? Math.max(0, soru - dogru - yanlis));
  const netRaw = item?.net ?? item?.score ?? item?.n ?? (dogru - (yanlis / 4));
  const net = Number(netRaw || 0);
  return { ders, soru: soru || (dogru + yanlis + bos), dogru, yanlis, bos, net };
};

const getNumberByKeys = (source = {}, keys = []) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null && source?.[key] !== '') {
      const value = Number(source[key]);
      if (!Number.isNaN(value)) return value;
    }
  }
  return null;
};

const buildSubjectFromRoot = (raw = {}, dersLabel = '', aliases = []) => {
  const aliasKeys = aliases.flatMap((alias) => ([
    `${alias}_dogru`, `${alias}Dogru`, `${alias}_d`, `${alias}D`, `${alias}_correct`,
  ]));
  const wrongKeys = aliases.flatMap((alias) => ([
    `${alias}_yanlis`, `${alias}Yanlis`, `${alias}_y`, `${alias}Y`, `${alias}_wrong`,
  ]));
  const emptyKeys = aliases.flatMap((alias) => ([
    `${alias}_bos`, `${alias}Bos`, `${alias}_b`, `${alias}B`, `${alias}_empty`,
  ]));
  const netKeys = aliases.flatMap((alias) => ([
    `${alias}_net`, `${alias}Net`, `${alias}_n`, `${alias}N`, `${alias}_score`,
  ]));
  const questionKeys = aliases.flatMap((alias) => ([
    `${alias}_soru`, `${alias}Soru`, `${alias}_q`, `${alias}Q`, `${alias}_question`,
  ]));

  const dogru = getNumberByKeys(raw, aliasKeys);
  const yanlis = getNumberByKeys(raw, wrongKeys);
  const bos = getNumberByKeys(raw, emptyKeys);
  const net = getNumberByKeys(raw, netKeys);
  const soru = getNumberByKeys(raw, questionKeys);
  const hasAny = [dogru, yanlis, bos, net, soru].some((x) => x !== null);
  if (!hasAny) return null;

  return normalizeDetayItem({
    ders: dersLabel,
    dogru: dogru ?? 0,
    yanlis: yanlis ?? 0,
    bos: bos ?? 0,
    net: net ?? ((dogru ?? 0) - ((yanlis ?? 0) / 4)),
    soru: soru ?? ((dogru ?? 0) + (yanlis ?? 0) + (bos ?? 0)),
  }, dersLabel);
};

const normalizeKarneRecord = (raw = {}) => {
  const adSoyadRaw = raw?.adSoyad || raw?.ad_soyad || raw?.ogrenci_adsoyad || raw?.name || raw?.student_name || '';
  const parsed = splitName(adSoyadRaw);
  const ad = String(raw?.ad || raw?.adi || parsed.ad || '').trim();
  const soyad = String(raw?.soyad || raw?.soyadi || parsed.soyad || '').trim();
  const adSoyad = `${ad} ${soyad}`.trim() || String(adSoyadRaw || '').trim();
  const numara = String(
    raw?.numara ??
    raw?.ogrenciNo ??
    raw?.ogrenci_no ??
    raw?.okulNo ??
    raw?.okul_no ??
    raw?.school_no ??
    '-'
  ).trim() || '-';
  const sinif = String(raw?.sinif || raw?.sinifi || raw?.class || '-');
  const kitapcik = String(raw?.kitapcik || raw?.booklet || raw?.kitapcik_turu || 'A');

  const arrayCandidates = [raw?.detaylar, raw?.dersSonuclari, raw?.lessonResults, raw?.dersler, raw?.branslar];
  const fromArray = arrayCandidates.find((item) => Array.isArray(item) && item.length > 0) || [];
  const objectCandidate = [raw?.detaylar, raw?.dersler, raw?.lesson_summary, raw?.brans_ozeti]
    .find((item) => item && !Array.isArray(item) && typeof item === 'object');
  const fromObject = objectCandidate
    ? Object.entries(objectCandidate).map(([ders, item]) => normalizeDetayItem(item, ders))
    : [];
  // Dizi detaylari varsa birincil kaynak odur; ozet objeleriyle karistirmayiz.
  const mergedDetaylar = (fromArray.length > 0 ? fromArray.map((x) => normalizeDetayItem(x)) : fromObject)
    .filter((item) => item?.ders);
  const detailMap = {};
  mergedDetaylar.forEach((item) => {
    const key = normalizeLessonToken(item.ders);
    const mevcut = detailMap[key];
    if (!mevcut) {
      detailMap[key] = { ...item };
      return;
    }
    const mevcutDoluluk = Number(mevcut?.dogru || 0) + Number(mevcut?.yanlis || 0) + Number(mevcut?.bos || 0);
    const yeniDoluluk = Number(item?.dogru || 0) + Number(item?.yanlis || 0) + Number(item?.bos || 0);
    const mevcutSoru = Number(mevcut?.soru || 0);
    const yeniSoru = Number(item?.soru || 0);
    // Aynı ders için farklı kaynaklardan tekrar eden kayıtları toplamayız; en dolu olanı seçeriz.
    if (yeniDoluluk > mevcutDoluluk || (yeniDoluluk === mevcutDoluluk && yeniSoru > mevcutSoru)) {
      detailMap[key] = { ...item };
    }
  });
  const rootSubjects = [
    buildSubjectFromRoot(raw, 'Türkçe', ['turkce', 'turk']),
    buildSubjectFromRoot(raw, 'Sosyal Bilgiler', ['sosyal', 'sos']),
    buildSubjectFromRoot(raw, 'Matematik', ['matematik', 'mat']),
    buildSubjectFromRoot(raw, 'Fen Bilimleri', ['fen']),
  ].filter(Boolean);

  rootSubjects.forEach((item) => {
    const key = normalizeLessonToken(item.ders);
    const mevcut = detailMap[key];
    // Root subject alanları bazı kayıtlarda hatalı/eksik gelebiliyor.
    // Bu nedenle mevcut detay varsa ezmeyip sadece eksik dersi tamamlıyoruz.
    if (!mevcut) {
      detailMap[key] = item;
    }
  });
  const detaylar = Object.values(detailMap);

  return {
    ...raw,
    ad,
    soyad,
    adSoyad,
    numara,
    sinif,
    kitapcik,
    detaylar,
  };
};

const extractDenemeSonuclari = (payload) => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.sonuclar)) return payload.data.sonuclar;
  if (Array.isArray(payload?.sonuclar)) return payload.sonuclar;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const summarizeWarnings = (warnings = []) => {
  const list = Array.isArray(warnings) ? warnings.filter(Boolean) : [];
  if (list.length === 0) return { summary: null, preview: [], extraCount: 0 };
  const shortCount = list.filter((w) => /beklenenden kisa/i.test(String(w))).length;
  const summary = shortCount > 0 ? `${shortCount} satırda cevap bloğu beklenenden kısa` : `${list.length} uyarı oluştu`;
  const preview = list.slice(0, 3);
  const extraCount = Math.max(0, list.length - preview.length);
  return { summary, preview, extraCount };
};

const safeParseJSON = (raw, fallback) => {
  try {
    return JSON.parse(raw);
  } catch (error) {
    return fallback;
  }
};

const clearOldResultCache = () => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('dp_deneme_sonuclari_'))
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {}
};

const safeSetLocalStorage = (key, value, options = {}) => {
  const { clearResultCacheOnQuota = false } = options;
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    const isQuota = error?.name === 'QuotaExceededError' || String(error?.message || '').toLowerCase().includes('quota');
    if (isQuota && clearResultCacheOnQuota) {
      try {
        clearOldResultCache();
        localStorage.setItem(key, value);
        return true;
      } catch (retryError) {}
    }
    return false;
  }
};

// jsPDF autoTable Türkçe karakter hatasını önlemek için özel dönüştürücü
const normalizeTR = (text) => {
  if (!text) return '';
  return text
    .replace(/Ğ/g, 'G').replace(/ğ/g, 'g')
    .replace(/Ü/g, 'U').replace(/ü/g, 'u')
    .replace(/Ş/g, 'S').replace(/ş/g, 's')
    .replace(/İ/g, 'I').replace(/ı/g, 'i')
    .replace(/Ö/g, 'O').replace(/ö/g, 'o')
    .replace(/Ç/g, 'C').replace(/ç/g, 'c');
};

// --- YOĞUN TABLO DÜZENİ KARNE ŞABLONU (PDF İÇİN) ---
const BireyselDetayliKarne = ({ student, sinavDetay, sinavOrtalamalari, tumSonuclar = [] }) => {
  const detaylar = Array.isArray(student?.detaylar) ? student.detaylar : [];
  const kazanimOzetiPdf = student?.kazanimOzetiPdf && typeof student.kazanimOzetiPdf === 'object'
    ? student.kazanimOzetiPdf
    : null;
  const buildKonuKartlari = (source) => {
    const rawCards = Array.isArray(source?.kartlar) ? source.kartlar : [];
    const dersler = Array.isArray(source?.dersler) ? source.dersler : [];
    const derived = [];
    dersler.forEach((dersItem) => {
      const ders = dersItem?.ders || '';
      const gelistirilecekler = Array.isArray(dersItem?.gelistirilecekler) ? dersItem.gelistirilecekler : [];
      const iyiOlanlar = Array.isArray(dersItem?.iyi_olanlar) ? dersItem.iyi_olanlar : [];
      [...gelistirilecekler, ...iyiOlanlar].forEach((k) => {
        derived.push({
          ders,
          konu: k?.konu || k?.konu_adi || k?.ad || '',
          soru: k?.soru ?? 0,
          dogru: k?.dogru ?? 0,
          yanlis: k?.yanlis ?? 0,
          basari: k?.basari ?? 0,
          durum: gelistirilecekler.includes(k) ? 'gelistirilmeli' : 'iyi',
        });
      });
    });
    const fromKonuAnaliz = [];
    const konuAnaliz = student?.konuAnaliz && typeof student.konuAnaliz === 'object' ? student.konuAnaliz : {};
    Object.entries(konuAnaliz).forEach(([ders, list]) => {
      const rows = Array.isArray(list) ? list : [];
      rows.forEach((k) => {
        fromKonuAnaliz.push({
          ders,
          konu: k?.konu || k?.konu_adi || k?.ad || '',
          soru: k?.soru ?? 0,
          dogru: k?.dogru ?? 0,
          yanlis: k?.yanlis ?? 0,
          basari: k?.basari ?? 0,
          durum: Number(k?.basari ?? 0) >= 70 ? 'iyi' : 'gelistirilmeli',
        });
      });
    });
    const merged = [...rawCards, ...derived, ...fromKonuAnaliz];
    const seen = new Set();
    return merged.filter((item) => {
      const ders = String(item?.ders || '').trim();
      const konu = String(item?.konu || item?.konu_adi || item?.ad || '').trim();
      if (!ders && !konu) return false;
      const key = `${normalizeLessonToken(ders)}__${normalizeLessonToken(konu)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const konuKartlari = buildKonuKartlari(kazanimOzetiPdf);
  const siraliKonuKartlari = (() => {
    const dersOrder = ['Türkçe', 'Sosyal Bilgiler', 'Matematik', 'Fen Bilimleri'];
    const groups = {};
    konuKartlari.forEach((item) => {
      const ders = normalizeDersAdi(item?.ders || 'Diğer');
      if (!groups[ders]) groups[ders] = [];
      groups[ders].push({ ...item, ders });
    });
    const sortedDersler = Object.keys(groups).sort((a, b) => {
      const ia = dersOrder.findIndex((x) => normalizeLessonToken(x) === normalizeLessonToken(a));
      const ib = dersOrder.findIndex((x) => normalizeLessonToken(x) === normalizeLessonToken(b));
      const va = ia === -1 ? 999 : ia;
      const vb = ib === -1 ? 999 : ib;
      if (va !== vb) return va - vb;
      return a.localeCompare(b, 'tr');
    });
    return sortedDersler.flatMap((ders) => groups[ders]);
  })();
  const sinavAdi = sinavDetay?.ad || 'SONUÇ BELGESİ';
  const sinavTarihi = sinavDetay?.tarih
    ? new Date(sinavDetay.tarih).toLocaleDateString('tr-TR')
    : new Date().toLocaleDateString('tr-TR');
  const ogrenciAdSoyad = student?.adSoyad || `${student?.ad || ''} ${student?.soyad || ''}`.trim() || '-';
  const puanDegeri = typeof student?.puan === 'number' ? student.puan : parseFloat(student?.puan || 0);
  const toplamSoru = detaylar.reduce((sum, item) => sum + Number(item?.soru || 0), 0);
  const toplamDogru = detaylar.reduce((sum, item) => sum + Number(item?.dogru || 0), 0);
  const toplamYanlis = detaylar.reduce((sum, item) => sum + Number(item?.yanlis || 0), 0);
  const toplamBos = Math.max(0, toplamSoru - toplamDogru - toplamYanlis);
  const toplamNet = detaylar.reduce((sum, item) => sum + Number(item?.net || 0), 0);
  const ayniSinifSayisi = tumSonuclar.filter((item) => String(item?.sinif || '') === String(student?.sinif || '')).length;
  const kurumKatilim = tumSonuclar.filter((item) => item?.tip === 'kayitli').length;
  const ilceKatilim = Number(sinavDetay?.katilim_ilce || sinavDetay?.ilce_katilim || 0);
  const ilKatilim = Number(sinavDetay?.katilim_il || sinavDetay?.il_katilim || 0);
  const genelKatilim = tumSonuclar.length;

  const shortDers = (name = '') => {
    const v = String(name || '').toLowerCase('tr-TR');
    if (v.includes('turk')) return 'TÜRK';
    if (v.includes('sosyal')) return 'SOS';
    if (v.includes('mat')) return 'MAT';
    if (v.includes('fen')) return 'FEN';
    return String(name || '').slice(0, 5).toUpperCase();
  };
  const calculateBasari = (item) => {
    const soru = Number(item?.soru || 0);
    if (soru <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((Number(item?.dogru || 0) / soru) * 100)));
  };
  const chartBase = detaylar.map((d) => ({
    label: shortDers(d?.ders),
    ogr: Number(d?.dogru || 0),
    sinif: Math.max(0, Number((sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0).toFixed?.(2) || sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0)),
    kurum: Math.max(0, Number((sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0).toFixed?.(2) || sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0) - 1),
    genel: Math.max(0, Number((sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0).toFixed?.(2) || sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0) - 2),
  }));
  const chartMax = Math.max(1, ...chartBase.flatMap((x) => [x.ogr, x.sinif, x.kurum, x.genel]));
  const rawGenelNot = String(kazanimOzetiPdf?.not || '').trim();
  const genelNot = /tek sayfaya sigmasi|tek sayfaya sığması/i.test(rawGenelNot)
    ? ''
    : rawGenelNot;

  return (
    <div
      id="gizli-karne-alani"
      className="w-[210mm] min-h-[297mm] bg-white print-root"
      style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#111827', padding: '7mm 8mm' }}
    >
      <style>{`
        #gizli-karne-alani .r-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        #gizli-karne-alani .r-table th, #gizli-karne-alani .r-table td { border: 1px solid #1f2937; padding: 2px 4px; font-size: 10px; vertical-align: middle; }
        #gizli-karne-alani .r-table th { background: #f3f4f6; font-weight: 700; }
        #gizli-karne-alani .r-tight th, #gizli-karne-alani .r-tight td { font-size: 8.6px; padding: 1px 3px; }
        #gizli-karne-alani .r-zebra tbody tr:nth-child(even) { background: #f9fafb; }
        #gizli-karne-alani .k-full { white-space: normal; line-height: 1.2; word-break: break-word; }
      `}</style>

      <table className="r-table">
        <thead>
          <tr>
            <th style={{ width: '48%', fontSize: 30, letterSpacing: 0.4 }}>SONUÇ BELGESİ</th>
            <th style={{ width: '52%', fontSize: 11 }}>{normalizeTR(String(sinavAdi || '').toUpperCase())}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{normalizeTR(`${sinavDetay?.il || 'OSMANIYE'} / ${sinavDetay?.ilce || 'MERKEZ'} / ${sinavDetay?.okul || 'ATATÜRK ANADOLU LİSESİ'}`)}</td>
            <td style={{ textAlign: 'center', fontWeight: 700 }}>DERSLERE GÖRE ANALİZ</td>
          </tr>
        </tbody>
      </table>

      <div style={{ display: 'grid', gridTemplateColumns: '48% 52%', gap: 0 }}>
        <div style={{ paddingRight: 4 }}>
          <table className="r-table" style={{ marginTop: 4 }}>
            <tbody>
              <tr><th style={{ width: '66%' }}>Öğrenci</th><th style={{ width: '18%' }}>Numara</th><th style={{ width: '16%' }}>Sınıf</th></tr>
              <tr><td style={{ fontWeight: 700 }}>{normalizeTR(ogrenciAdSoyad)}</td><td style={{ textAlign: 'center' }}>{student?.numara || '-'}</td><td style={{ textAlign: 'center' }}>{student?.sinif || '-'}</td></tr>
            </tbody>
          </table>

          <table className="r-table" style={{ marginTop: 4 }}>
            <tbody>
              <tr>
                <th style={{ width: '16%' }}>Puan Türü</th><th style={{ width: '20%' }}>Puan</th><th style={{ width: '18%' }}>Genel Ort.</th>
                <th colSpan={5}>Dereceler</th>
              </tr>
              <tr>
                <td style={{ textAlign: 'center' }}>{student?.sinav_turu || 'TYT'}</td>
                <td style={{ textAlign: 'center', fontWeight: 700 }}>{Number.isFinite(puanDegeri) ? puanDegeri.toFixed(3) : '-'}</td>
                <td style={{ textAlign: 'center' }}>{(toplamNet / Math.max(1, detaylar.length)).toFixed(2)}</td>
                <td style={{ textAlign: 'center' }}>Snf</td><td style={{ textAlign: 'center' }}>Kurum</td><td style={{ textAlign: 'center' }}>İlçe</td><td style={{ textAlign: 'center' }}>İl</td><td style={{ textAlign: 'center' }}>Genel</td>
              </tr>
              <tr>
                <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>Katılımlar:</td>
                <td style={{ textAlign: 'center' }}>{ayniSinifSayisi || '-'}</td>
                <td style={{ textAlign: 'center' }}>{kurumKatilim || '-'}</td>
                <td style={{ textAlign: 'center' }}>{ilceKatilim || '-'}</td>
                <td style={{ textAlign: 'center' }}>{ilKatilim || '-'}</td>
                <td style={{ textAlign: 'center' }}>{genelKatilim || '-'}</td>
              </tr>
            </tbody>
          </table>

          <table className="r-table r-zebra" style={{ marginTop: 4 }}>
            <thead>
              <tr>
                <th style={{ width: '30%' }}>Ders</th><th>Soru</th><th>Doğru</th><th>Yanlış</th><th>Net</th><th>Başarı%</th><th>Sınıf</th><th>Kurum</th><th>Genel</th>
              </tr>
            </thead>
            <tbody>
              {detaylar.map((d, i) => (
                <tr key={`${d?.ders || 'd'}_${i}`}>
                  <td>{normalizeTR(d?.ders || '-')}</td>
                  <td style={{ textAlign: 'center' }}>{d?.soru ?? 0}</td>
                  <td style={{ textAlign: 'center' }}>{d?.dogru ?? 0}</td>
                  <td style={{ textAlign: 'center' }}>{d?.yanlis ?? 0}</td>
                  <td style={{ textAlign: 'center' }}>{Number(d?.net || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'center' }}>{calculateBasari(d)}</td>
                  <td style={{ textAlign: 'center' }}>{Number(sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'center' }}>{Math.max(0, Number(sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0) - 0.5).toFixed(2)}</td>
                  <td style={{ textAlign: 'center' }}>{Math.max(0, Number(sinavOrtalamalari?.[d?.ders]?.ortalamaNet || 0) - 1).toFixed(2)}</td>
                </tr>
              ))}
              <tr style={{ background: '#e5e7eb', fontWeight: 700 }}>
                <td style={{ textAlign: 'right' }}>Toplam</td>
                <td style={{ textAlign: 'center' }}>{toplamSoru}</td>
                <td style={{ textAlign: 'center' }}>{toplamDogru}</td>
                <td style={{ textAlign: 'center' }}>{toplamYanlis}</td>
                <td style={{ textAlign: 'center' }}>{toplamNet.toFixed(2)}</td>
                <td style={{ textAlign: 'center' }}>{Math.round((toplamDogru / Math.max(1, toplamSoru)) * 100)}</td>
                <td colSpan={3} />
              </tr>
            </tbody>
          </table>

          <table className="r-table r-tight" style={{ marginTop: 4 }}>
            <thead>
              <tr><th style={{ width: '26%' }}>Ders</th><th style={{ width: '6%' }}>K</th><th>Kazanım / Cevap Anahtarı</th></tr>
            </thead>
            <tbody>
              {detaylar.map((d, i) => (
                <tr key={`ca_${i}`}>
                  <td>{normalizeTR(d?.ders || '-')}</td>
                  <td style={{ textAlign: 'center' }}>{student?.kitapcik || 'A'}</td>
                  <td>{normalizeTR((student?.dersCevaplari?.[d?.ders] || '').slice(0, 38) || '-')}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: 4, border: '1px solid #1f2937', padding: '3px 4px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, chartBase.length)}, 1fr)`, gap: 4, alignItems: 'end', height: 78 }}>
              {chartBase.map((s, i) => (
                <div key={`bar_${i}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, alignItems: 'end' }}>
                  <div style={{ background: '#dc2626', height: `${Math.max(3, (s.ogr / chartMax) * 54)}px` }} />
                  <div style={{ background: '#059669', height: `${Math.max(3, (s.sinif / chartMax) * 54)}px` }} />
                  <div style={{ background: '#d97706', height: `${Math.max(3, (s.kurum / chartMax) * 54)}px` }} />
                  <div style={{ background: '#4f46e5', height: `${Math.max(3, (s.genel / chartMax) * 54)}px` }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, chartBase.length)}, 1fr)`, gap: 4, marginTop: 2, fontSize: 8 }}>
              {chartBase.map((s, i) => <div key={`lbl_${i}`} style={{ textAlign: 'center', fontWeight: 700 }}>{s.label}</div>)}
            </div>
            <div style={{ display: 'flex', gap: 10, fontSize: 8, marginTop: 3 }}>
              <span><span style={{ color: '#dc2626' }}>■</span> Öğr.</span>
              <span><span style={{ color: '#059669' }}>■</span> Sınıf</span>
              <span><span style={{ color: '#d97706' }}>■</span> Kurum</span>
              <span><span style={{ color: '#4f46e5' }}>■</span> Genel</span>
            </div>
          </div>
        </div>

        <div style={{ paddingLeft: 4 }}>
          <table className="r-table r-tight r-zebra" style={{ marginTop: 4 }}>
            <thead>
              <tr>
                <th style={{ width: '62%' }}>Konu</th><th style={{ width: '8%' }}>S</th><th style={{ width: '8%' }}>D</th><th style={{ width: '8%' }}>Y</th><th style={{ width: '14%' }}>B%</th>
              </tr>
            </thead>
            <tbody>
              {siraliKonuKartlari.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 8 }}>Kazanım analizi bulunamadı</td></tr>
              )}
              {siraliKonuKartlari.map((k, i) => {
                const konuLabel = k?.konu || k?.konu_adi || k?.ad || '-';
                const prev = i > 0 ? siraliKonuKartlari[i - 1] : null;
                const dersDegisti = !prev || normalizeLessonToken(prev?.ders) !== normalizeLessonToken(k?.ders);
                return (
                <tr key={`kz_${i}`}>
                  <td>
                    {dersDegisti && (
                      <div style={{ fontWeight: 700, fontSize: 8.4, marginBottom: 1 }}>{normalizeTR(String(k?.ders || '-'))}</div>
                    )}
                    <div className="k-full" style={{ fontSize: 7.6 }}>{normalizeTR(String(konuLabel))}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>{Number(k?.soru || 0)}</td>
                  <td style={{ textAlign: 'center' }}>{Number(k?.dogru || 0)}</td>
                  <td style={{ textAlign: 'center' }}>{Number(k?.yanlis || 0)}</td>
                  <td style={{ textAlign: 'center', fontWeight: 700 }}>{Number(k?.basari || 0)}</td>
                </tr>
              );
              })}
            </tbody>
          </table>
          <div style={{ marginTop: 4, border: '1px solid #1f2937', padding: '4px 6px', fontSize: 8.4, color: '#4b5563', minHeight: 30 }}>
            {genelNot || `Rapor tarihi: ${sinavTarihi}. Kitapçık: ${student?.kitapcik || 'A'}. Tüm kazanımlar listelenmiştir.`}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 4, borderTop: '1px solid #9ca3af', paddingTop: 2, textAlign: 'center', fontSize: 8, color: '#6b7280' }}>
        asdersistem
      </div>
    </div>
  );
};


export default function DenemeTopluGirisPage() {
  const [aktifSekme, setAktifSekme] = useState('list'); 
  
  const [sinavlar, setSinavlar] = useState([]);
  const [seciliSinav, setSeciliSinav] = useState('');
  const [sonuclar, setSonuclar] = useState([]);
  const [optikSablonlar, setOptikSablonlar] = useState([]);
  const [txtUploadMeta, setTxtUploadMeta] = useState(null);
  
  const [showYeniSinav, setShowYeniSinav] = useState(false);
  const [yeniSinavForm, setYeniSinavForm] = useState({ ad: '', tarih: new Date().toISOString().slice(0,10), tur: 'TYT' });
  const [fmtDosyasi, setFmtDosyasi] = useState(null);
  const [fmtAd, setFmtAd] = useState('');
  
  const [txtDosyalari, setTxtDosyalari] = useState([]); 
  
  const [seciliSablonId, setSeciliSablonId] = useState('');
  const [fmtYukleniyor, setFmtYukleniyor] = useState(false);
  const [txtYukleniyor, setTxtYukleniyor] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [cevapAnahtariKaydediliyor, setCevapAnahtariKaydediliyor] = useState(false);
  const [showTxtSablonOgren, setShowTxtSablonOgren] = useState(false);
  const [txtOgrenFiles, setTxtOgrenFiles] = useState([]);
  const [txtOgrenSamples, setTxtOgrenSamples] = useState([]);
  const [txtOgrenSampleIndex, setTxtOgrenSampleIndex] = useState(0);
  const [txtOgrenRange, setTxtOgrenRange] = useState({ start: '', end: '' });
  const [txtOgrenActiveField, setTxtOgrenActiveField] = useState('AdSoyad');
  const [txtOgrenSelectedSample, setTxtOgrenSelectedSample] = useState(0);
  const [txtOgrenPreviewSelection, setTxtOgrenPreviewSelection] = useState('');
  const [txtOgrenDersListesi, setTxtOgrenDersListesi] = useState(['Türkçe', 'Sosyal Bilgiler', 'Matematik', 'Fen Bilimleri']);
  const [txtOgrenYeniDers, setTxtOgrenYeniDers] = useState('');
  const [txtOgrenSelections, setTxtOgrenSelections] = useState([]);
  const [txtOgrenLineMode, setTxtOgrenLineMode] = useState('single');
  const [txtOgrenSablonId, setTxtOgrenSablonId] = useState('');
  const [txtOgrenSablonAd, setTxtOgrenSablonAd] = useState('');
  const [txtOgrenError, setTxtOgrenError] = useState(null);
  const [txtOgrenLoading, setTxtOgrenLoading] = useState(false);
  const [flowError, setFlowError] = useState(null);
  const [kazanimSablonu, setKazanimSablonu] = useState({});
  
  const fmtInputRef = useRef(null);
  const txtInputRef = useRef(null);
  const txtOgrenFileRef = useRef(null);
  const txtOgrenLineRef = useRef(null);
  const [arama, setArama] = useState('');
  const [seciliKarne, setSeciliKarne] = useState(null);
  const [aktifPrintOgrenci, setAktifPrintOgrenci] = useState(null);
  const [pdfOlusturuluyor, setPdfOlusturuluyor] = useState(false);
  const [pdfProgress, setPdfProgress] = useState('');
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const sonucCacheKey = seciliSinav ? `dp_deneme_sonuclari_${seciliSinav}` : null;

  const [sinavTuru, setSinavTuru] = useState('TYT');
  const [kitapciklarListesi, setKitapciklarListesi] = useState(['A', 'B']);
  const [aktifKitapcik, setAktifKitapcik] = useState('A');
  const [cevapAnahtari, setCevapAnahtari] = useState({
    A: EXAM_SUBJECTS['TYT'].reduce((acc, curr) => ({ ...acc, [curr]: '' }), {}),
    B: EXAM_SUBJECTS['TYT'].reduce((acc, curr) => ({ ...acc, [curr]: '' }), {})
  });
  const {
    runSaveAndUpload,
    loading: txtFlowLoading,
    savingTemplate,
    uploadingTxt,
    error: txtFlowError,
  } = useTxtLearningFlow(seciliSinav);

  const getCachedSonuclar = (sinavId) => {
    if (!sinavId) return [];
    return safeParseJSON(localStorage.getItem(`dp_deneme_sonuclari_${sinavId}`) || '[]', []);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadSinavlar = async (options = {}) => {
    try {
      const { data } = await axios.get('/denemeler');
      if (data.success && Array.isArray(data.data)) {
        setSinavlar(data.data);
        if (options.selectId) setSeciliSinav(options.selectId);
        else if (!seciliSinav && data.data.length > 0) setSeciliSinav(data.data[0].id);
        return data.data;
      }
    } catch (error) {
      const yerelSinavlar = JSON.parse(localStorage.getItem('dp_sinavlar') || '[]').map((sinav) => ({
        ...sinav, _localOnly: true,
      }));
      setSinavlar(yerelSinavlar);
      if (options.selectId) setSeciliSinav(options.selectId);
      else if (yerelSinavlar.length > 0 && !seciliSinav) setSeciliSinav(yerelSinavlar[0].id);
      return yerelSinavlar;
    }
    return [];
  };

  useEffect(() => { loadSinavlar(); }, []);

  useEffect(() => {
    const loadOptikSablonlar = async () => {
      try {
        const { data } = await axios.get('/denemeler/optik-sablonlar');
        setOptikSablonlar(Array.isArray(data?.data) ? data.data : []);
      } catch (error) { setOptikSablonlar([]); }
    };
    loadOptikSablonlar();
  }, []);

  useEffect(() => {
    if (!seciliSinav) return;
    const saved = localStorage.getItem(`dp_optik_sablon_${seciliSinav}`);
    if (saved) setSeciliSablonId(saved);
  }, [seciliSinav]);

  useEffect(() => {
    if (!seciliSinav) return;
    if (seciliSablonId) localStorage.setItem(`dp_optik_sablon_${seciliSinav}`, seciliSablonId);
  }, [seciliSinav, seciliSablonId]);

  useEffect(() => {
    if (!seciliSinav) { setSonuclar([]); return; }
    const loadSonuclar = async () => {
      const cached = sonucCacheKey ? safeParseJSON(localStorage.getItem(sonucCacheKey) || '[]', []) : [];
      try {
        const { data } = await axios.get(`/denemeler/${seciliSinav}/karneler`);
        if (data.success) {
          const apiSonuclar = extractDenemeSonuclari(data);
          const birlesikSonuclar = apiSonuclar.length >= cached.length ? apiSonuclar : cached;
          setSonuclar(birlesikSonuclar);
          if (sonucCacheKey) safeSetLocalStorage(sonucCacheKey, JSON.stringify(birlesikSonuclar), { clearResultCacheOnQuota: true });
          return;
        }
      } catch (error) {}
      setSonuclar(cached);
    };
    loadSonuclar();
  }, [seciliSinav, sonucCacheKey]);

  useEffect(() => {
    setTxtUploadMeta(null);
    setUploadResult(null);
    setFlowError(null);
  }, [seciliSinav]);

  useEffect(() => {
    if (!txtFlowError) return;
    setFlowError(txtFlowError.response?.data || txtFlowError.message || null);
  }, [txtFlowError]);

  useEffect(() => {
    const fromKarne = Object.keys(seciliKarne?.konuAnaliz || {});
    const fromSablon = Object.keys(getAktifKazanimMap() || {});
    const ilkDers = fromKarne[0] || fromSablon[0] || null;
    setExpandedLesson(ilkDers);
  }, [seciliKarne, kazanimSablonu]);

  const handleYeniSinavKaydet = async () => {
    if (!yeniSinavForm.ad || !yeniSinavForm.tarih) return toast.error('Sınav adı ve tarih zorunludur!');
    try {
      const { data } = await axios.post('/denemeler', yeniSinavForm);
      const apiSinav = data?.data || data?.sinav || null;
      const yeniSinavData = Array.isArray(apiSinav) ? null : apiSinav;
      const yeniSinavFallback = { id: Date.now(), ad: yeniSinavForm.ad, tarih: yeniSinavForm.tarih, tur: yeniSinavForm.tur, katilim: 0 };
      
      if (Array.isArray(apiSinav)) {
        setSinavlar(apiSinav);
        const sonEklenen = apiSinav.find((item) => item.ad === yeniSinavForm.ad && item.tarih === yeniSinavForm.tarih) || apiSinav[0];
        if (sonEklenen?.id) setSeciliSinav(sonEklenen.id);
      } else {
        const yeni = yeniSinavData || yeniSinavFallback;
        setSinavlar((prev) => [yeni, ...prev.filter((item) => String(item.id) !== String(yeni.id))]);
        setSeciliSinav(yeni.id);
      }
      await loadSinavlar({ selectId: (yeniSinavData || yeniSinavFallback).id });
      setShowYeniSinav(false);
      setYeniSinavForm({ ad: '', tarih: new Date().toISOString().slice(0,10), tur: 'TYT' });
      toast.success('Sınav veritabanına kaydedildi!');
    } catch (error) { toast.error(error.response?.data?.message || 'Sınav backendde oluşturulamadı.'); }
  };

  const handleSinavSil = async (sinav) => {
    if (!sinav?.id) return;
    const onay = window.confirm(`"${sinav.ad}" denemesini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`);
    if (!onay) return;

    try {
      await axios.delete(`/denemeler/${sinav.id}`);
      const kalanSinavlar = sinavlar.filter((item) => String(item.id) !== String(sinav.id));
      setSinavlar(kalanSinavlar);
      if (String(seciliSinav) === String(sinav.id)) {
        setSeciliSinav(kalanSinavlar[0]?.id || '');
      }
      localStorage.removeItem(`dp_deneme_sonuclari_${sinav.id}`);
      localStorage.removeItem(`dp_optik_sablon_${sinav.id}`);
      toast.success('Deneme silindi.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Deneme silinemedi.');
    }
  };

  const loadSonuclarForSinav = async (sinavId, options = {}) => {
    if (!sinavId) { setSonuclar([]); return []; }
    const cacheKey = `dp_deneme_sonuclari_${sinavId}`;
    const cached = safeParseJSON(localStorage.getItem(cacheKey) || '[]', []);
    try {
      const { data } = await axios.get(`/denemeler/${sinavId}/karneler`);
      if (data.success) {
        const apiSonuclar = extractDenemeSonuclari(data);
        const birlesikSonuclar = apiSonuclar.length >= cached.length ? apiSonuclar : cached;
        setSonuclar(birlesikSonuclar);
        safeSetLocalStorage(cacheKey, JSON.stringify(birlesikSonuclar), { clearResultCacheOnQuota: true });
        if (options.showToast) toast.success('Sonuç listesi güncellendi.');
        return birlesikSonuclar;
      }
    } catch (error) { if (options.showToast) toast.error(error.response?.data?.message || 'Sonuçlar yenilenemedi.'); }
    setSonuclar(cached);
    return cached;
  };

  const handleFmtUpload = async () => {
    if (!fmtDosyasi) return toast.error('Önce bir .fmt dosyası seçin.');
    if (!fmtDosyasi.name.toLowerCase().endsWith('.fmt')) return toast.error('Sadece .fmt uzantılı dosya yükleyebilirsiniz.');

    const formData = new FormData();
    formData.append('file', fmtDosyasi);
    if (fmtAd.trim()) formData.append('ad', fmtAd.trim());

    setFmtYukleniyor(true);
    try {
      const { data } = await axios.post('/denemeler/optik-sablonlar/fmt-yukle', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const yeniSablon = data?.data || data?.sablon || null;
      toast.success('Optik şablonu yüklendi.');
      setFmtDosyasi(null); setFmtAd('');
      if (fmtInputRef.current) fmtInputRef.current.value = '';
      
      const { data: sablonData } = await axios.get('/denemeler/optik-sablonlar');
      const sablonlar = Array.isArray(sablonData?.data) ? sablonData.data : [];
      setOptikSablonlar(sablonlar);
      if (yeniSablon?.id) setSeciliSablonId(String(yeniSablon.id));
      else if (sablonlar.length > 0) setSeciliSablonId(String(sablonlar[0].id));
    } catch (error) { toast.error(error.response?.data?.message || 'FMT şablonu yüklenemedi.'); } 
    finally { setFmtYukleniyor(false); }
  };

  const handleSinavTuruDegis = (yeniTur) => {
    setSinavTuru(yeniTur);
    const dersler = uniqLessonLabels(EXAM_SUBJECTS[yeniTur] || []);
    const yeniAnahtarYapisi = {};
    kitapciklarListesi.forEach((k) => {
      yeniAnahtarYapisi[k] = dersler.reduce((acc, curr) => ({ ...acc, [curr]: '' }), {});
    });
    setCevapAnahtari(yeniAnahtarYapisi);
    setTxtOgrenDersListesi(dersler);
    setKazanimSablonu((prev) => ensureKazanimForLessons(prev, dersler, kitapciklarListesi));
  };

  const handleYeniKitapcikEkle = () => {
    const siradakiHarf = String.fromCharCode(65 + kitapciklarListesi.length);
    setKitapciklarListesi([...kitapciklarListesi, siradakiHarf]);
    const dersler = uniqLessonLabels(EXAM_SUBJECTS[sinavTuru] || txtOgrenDersListesi);
    setCevapAnahtari(prev => ({ ...prev, [siradakiHarf]: dersler.reduce((acc, curr) => ({ ...acc, [curr]: '' }), {}) }));
    setKazanimSablonu((prev) => ensureKazanimForLessons(prev, dersler, [...kitapciklarListesi, siradakiHarf]));
    setAktifKitapcik(siradakiHarf);
  };

  const handleAnswerKeyChange = (ders, value) => {
    setCevapAnahtari(prev => ({ ...prev, [aktifKitapcik]: { ...prev[aktifKitapcik], [ders]: value.toUpperCase().replace(/[^ABCDE*X]/g, '') } }));
  };

  const handleCevapAnahtariFmtDoldur = async () => {
    if (!fmtDosyasi) {
      toast.error('Önce bir .fmt dosyası seçin.');
      return;
    }
    try {
      const rawText = await fmtDosyasi.text();
      const bookletRegex = new RegExp(`\\b${aktifKitapcik}\\s*K[İI]TAPC[İI]K[^\\n\\r]*([\\s\\S]*?)(?=\\b[A-Z]\\s*K[İI]TAPC[İI]K|$)`, 'i');
      const bookletMatch = rawText.match(bookletRegex);
      const sourceText = bookletMatch?.[1] || rawText;
      const allAnswers = normalizeAnswerChars(sourceText);
      if (!allAnswers) {
        toast.error('FMT içinde okunabilir cevap dizisi bulunamadı.');
        return;
      }

      const plan = txtOgrenDersListesi.map((ders) => {
        const mevcutLen = String(cevapAnahtari?.[aktifKitapcik]?.[ders] || '').replace(/\s+/g, '').length;
        const tahmini = getEstimatedQuestionCount(sinavTuru, ders);
        return { ders, len: tahmini || mevcutLen || 0 };
      });
      const plannedTotal = plan.reduce((sum, item) => sum + item.len, 0);

      if (plannedTotal > 0 && allAnswers.length < plannedTotal) {
        toast.error(`FMT cevabı kısa: ${allAnswers.length} karakter. Beklenen en az ${plannedTotal}.`);
        return;
      }

      let cursor = 0;
      const parsed = {};
      plan.forEach((item, idx) => {
        if (item.len > 0) {
          parsed[item.ders] = allAnswers.slice(cursor, cursor + item.len);
          cursor += item.len;
          return;
        }
        if (idx === plan.length - 1) parsed[item.ders] = allAnswers.slice(cursor);
      });

      if (plannedTotal === 0 && txtOgrenDersListesi.length > 0) {
        parsed[txtOgrenDersListesi[0]] = allAnswers;
      }

      setCevapAnahtari((prev) => ({
        ...prev,
        [aktifKitapcik]: {
          ...(prev[aktifKitapcik] || {}),
          ...parsed,
        },
      }));
      toast.success(`FMT'den ${aktifKitapcik} kitapçığı için cevap anahtarı dolduruldu.`);
    } catch (error) {
      toast.error('FMT dosyası okunamadı.');
    }
  };

  const handleCevapAnahtariKaydet = async () => {
    if (!ensureServerBackedSinav()) return;
    let doluMu = false;
    Object.values(cevapAnahtari).forEach(kitapcik => { Object.values(kitapcik).forEach(cevaplar => { if (String(cevaplar || '').trim().length > 0) doluMu = true; }); });
    if (!doluMu) return toast.error('En az bir kitapçık için cevap anahtarı girin.');
    const normalizedKazanim = {};
    for (const booklet of kitapciklarListesi) {
      const b = String(booklet || '').toUpperCase();
      normalizedKazanim[b] = {};
      for (const ders of txtOgrenDersListesi) {
        const key = normalizeDersAdi(ders);
        const list = Array.isArray(kazanimSablonu?.[b]?.[key]) ? kazanimSablonu[b][key] : [];
        const mapped = [];
        for (const row of list) {
          if (!String(row?.start || '').trim() && !String(row?.end || '').trim() && !String(row?.konu || '').trim()) continue;
          const start = Number(row?.start);
          const end = Number(row?.end);
          const konu = String(row?.konu || '').trim();
          if (!Number.isInteger(start) || start <= 0 || !Number.isInteger(end) || end <= 0 || end < start || !konu) {
            return toast.error(`${b} kitapçığı • ${key} için kazanım satırları geçersiz. (start/end pozitif tam sayı, end>=start, konu zorunlu)`);
          }
          mapped.push({ start, end, konu });
        }
        normalizedKazanim[b][key] = mapped;
      }
    }

    setCevapAnahtariKaydediliyor(true);
    try {
      await axios.post(`/denemeler/${seciliSinav}/cevap-anahtari`, {
        tur: sinavTuru,
        kitapciklar: cevapAnahtari,
        kazanim_sablonu: {
          dersler: normalizedKazanim,
        },
      });
      setKazanimSablonu((prev) => ensureKazanimForLessons(prev, txtOgrenDersListesi, kitapciklarListesi));
      toast.success('Cevap anahtarı ve kazanım şablonu kaydedildi.');
    } catch (error) { toast.error(error.response?.data?.message || 'Cevap anahtarı kaydedilemedi.'); } 
    finally { setCevapAnahtariKaydediliyor(false); }
  };

  const handleTxtUpload = async () => {
    if (!ensureServerBackedSinav()) return;
    if (txtFlowLoading || savingTemplate || uploadingTxt) return;
    if (txtDosyalari.length === 0) return toast.error('Önce en az bir .txt sonuç dosyası seçin.');

    setTxtYukleniyor(true);
    setFlowError(null);
    try {
      const data = await uploadTxtResults({
        denemeId: seciliSinav,
        files: txtDosyalari,
        sablonId: seciliSablonId || undefined,
      });
      const uploadMeta = data?.meta || data?.data?.meta || null;
      setTxtUploadMeta(uploadMeta);
      setUploadResult({ sonuclar: extractDenemeSonuclari(data), meta: uploadMeta });
      if (txtInputRef.current) txtInputRef.current.value = '';
      setTxtDosyalari([]); 
      toast.success(data?.message || 'TXT sonuç dosyaları başarıyla işlendi.');
      const uploadSonuclari = extractDenemeSonuclari(data);
      if (uploadSonuclari.length > 0) {
        setSonuclar(uploadSonuclari);
        safeSetLocalStorage(`dp_deneme_sonuclari_${seciliSinav}`, JSON.stringify(uploadSonuclari), { clearResultCacheOnQuota: true });
      }
      await loadSonuclarForSinav(seciliSinav);
      setAktifSekme('karneler');
    } catch (error) {
      const errorMeta = error.response?.data?.meta || error.response?.data?.data?.meta || null;
      if (errorMeta) setTxtUploadMeta(errorMeta);
      setUploadResult({ sonuclar: [], meta: errorMeta });
      setFlowError(error.response?.data || error.message || null);
      toast.error(error.response?.data?.message || 'TXT sonuç dosyaları işlenemedi.');
    } 
    finally { setTxtYukleniyor(false); }
  };

  const resetTxtOgren = () => {
    setTxtOgrenFiles([]);
    setTxtOgrenSamples([]);
    setTxtOgrenSampleIndex(0);
    setTxtOgrenRange({ start: '', end: '' });
    setTxtOgrenSelections([]);
    setTxtOgrenLineMode('single');
    setTxtOgrenSablonId('');
    setTxtOgrenSablonAd('');
    setTxtOgrenError(null);
    setTxtOgrenLoading(false);
    setTxtOgrenActiveField(TXT_TEMPLATE_BASE_FIELDS[0]);
    setTxtOgrenSelectedSample(0);
    setTxtOgrenPreviewSelection('');
    setTxtOgrenDersListesi(['Türkçe', 'Sosyal Bilgiler', 'Matematik', 'Fen Bilimleri']);
    setTxtOgrenYeniDers('');
  };

  const extractTxtSamples = (payload) => {
    const raw =
      payload?.data?.samples ||
      payload?.samples ||
      payload?.data?.lines ||
      payload?.lines ||
      payload?.data?.preview ||
      [];
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => {
      if (typeof item === 'string') return item;
      return item?.line || item?.text || item?.content || '';
    }).filter(Boolean);
  };

  const handleTxtOgrenPreview = async () => {
    if (txtOgrenFiles.length === 0) {
      toast.error('Önce TXT dosyalarını seçin.');
      return;
    }
    setTxtOgrenLoading(true);
    setTxtOgrenError(null);
    try {
      const data = await previewTxtTemplate(txtOgrenFiles);
      const samples = extractTxtSamples(data);
      if (samples.length === 0) {
        toast.error('Önizleme satırı bulunamadı.');
        setTxtOgrenSamples([]);
        return;
      }
      setTxtOgrenSamples(samples);
      setTxtOgrenSampleIndex(0);
      setTxtOgrenSelectedSample(0);
      setTxtOgrenActiveField('AdSoyad');
    } catch (error) {
      const message = error.response?.data?.message || 'TXT önizleme başarısız oldu.';
      const debug = error.response?.data?.debug || error.response?.data || null;
      setTxtOgrenError({ message, debug });
      toast.error(message);
    } finally {
      setTxtOgrenLoading(false);
    }
  };

  const handleTxtOgrenRangeFromSelect = (e) => {
    const start = e.target.selectionStart;
    const end = e.target.selectionEnd;
    if (start === end) return;
    const currentLine = txtOgrenSamples[txtOgrenSelectedSample] || '';
    const selectedText = currentLine.slice(start, end);
    setTxtOgrenRange({ start: start + 1, end });
    setTxtOgrenPreviewSelection(selectedText);
  };

  const txtOgrenFieldList = [...TXT_TEMPLATE_BASE_FIELDS, ...txtOgrenDersListesi];

  const saveTxtOgrenSelection = (field, { advance = false } = {}) => {
    const start = parseInt(txtOgrenRange.start, 10);
    const end = parseInt(txtOgrenRange.end, 10);
    if (!field || !start || !end || start > end) return false;
    setTxtOgrenSelections(prev => {
      const next = prev.filter(item => item.field !== field);
      next.push({ field, start, end });
      return next;
    });
    if (advance) {
      const currentIndex = txtOgrenFieldList.indexOf(field);
      const nextField = txtOgrenFieldList[currentIndex + 1];
      if (nextField) setTxtOgrenActiveField(nextField);
    }
    return true;
  };

  const handleTxtOgrenAddSelection = () => {
    const field = txtOgrenActiveField;
    if (!field) return toast.error('Alan adı seçin.');
    const saved = saveTxtOgrenSelection(field, { advance: true });
    if (!saved) return toast.error('Aralık geçersiz. Başlangıç <= Bitiş olmalı.');

    setTxtOgrenRange({ start: '', end: '' });
    setTxtOgrenPreviewSelection('');
  };

  const handleTxtOgrenRemoveSelection = (field) => {
    setTxtOgrenSelections(prev => prev.filter(item => item.field !== field));
  };

  const handleTxtOgrenFieldPick = (field) => {
    const start = parseInt(txtOgrenRange.start, 10);
    const end = parseInt(txtOgrenRange.end, 10);
    if (txtOgrenActiveField && start && end && start <= end) {
      saveTxtOgrenSelection(txtOgrenActiveField);
      setTxtOgrenRange({ start: '', end: '' });
    }
    setTxtOgrenActiveField(field);
    const mevcut = txtOgrenSelections.find(item => item.field === field);
    if (mevcut) {
      setTxtOgrenRange({ start: mevcut.start, end: mevcut.end });
    } else {
      setTxtOgrenRange({ start: '', end: '' });
    }
    setTxtOgrenPreviewSelection('');
  };

  const handleTxtOgrenSamplePick = (index) => {
    setTxtOgrenSelectedSample(index);
    setTxtOgrenSampleIndex(index);
    setTxtOgrenPreviewSelection('');
  };

  const handleTxtOgrenDersEkle = () => {
    const yeni = canonicalLessonLabel(txtOgrenYeniDers || '');
    if (!yeni) return;
    if (txtOgrenDersListesi.some((d) => normalizeLessonToken(d) === normalizeLessonToken(yeni))) {
      toast.error('Bu ders zaten listede var.');
      return;
    }
    setTxtOgrenDersListesi(prev => uniqLessonLabels([...prev, yeni]));
    setKazanimSablonu((prev) => ensureKazanimForLessons(prev, [...txtOgrenDersListesi, yeni], kitapciklarListesi));
    setCevapAnahtari(prev => {
      const next = { ...prev };
      kitapciklarListesi.forEach(k => {
        next[k] = { ...(next[k] || {}), [yeni]: '' };
      });
      return next;
    });
    setTxtOgrenYeniDers('');
  };

  const handleTxtOgrenDersSil = (ders) => {
    setTxtOgrenDersListesi(prev => prev.filter(d => d !== ders));
    setTxtOgrenSelections(prev => prev.filter((item) => item.field !== ders));
    if (txtOgrenActiveField === ders) setTxtOgrenActiveField(TXT_TEMPLATE_BASE_FIELDS[0]);
    setCevapAnahtari(prev => {
      const next = { ...prev };
      kitapciklarListesi.forEach(k => {
        const clone = { ...(next[k] || {}) };
        delete clone[ders];
        next[k] = clone;
      });
      return next;
    });
    setKazanimSablonu((prev) => {
      const next = { ...prev };
      const dersKey = normalizeDersAdi(ders);
      kitapciklarListesi.forEach((k) => {
        const b = String(k || '').toUpperCase();
        if (!next[b] || typeof next[b] !== 'object' || Array.isArray(next[b])) return;
        const clone = { ...next[b] };
        delete clone[dersKey];
        next[b] = clone;
      });
      return next;
    });
  };

  const handleTxtOgrenAutoSplitAnswers = () => {
    const fieldOrder = txtOgrenDersListesi.slice();
    const start = parseInt(txtOgrenRange.start, 10);
    const end = parseInt(txtOgrenRange.end, 10);
    if (!start || !end || start > end) {
      toast.error('Önce tüm cevap bloğunu seçin.');
      return;
    }
    const aktifCevapAnahtari = cevapAnahtari?.[aktifKitapcik] || {};
    const lengths = {};
    const missing = [];
    fieldOrder.forEach((field) => {
      const raw = String(aktifCevapAnahtari?.[field] || '');
      const len = raw.replace(/\s+/g, '').length;
      lengths[field] = len;
      if (!len) missing.push(getTxtTemplateFieldLabel(field));
    });
    if (missing.length > 0) {
      toast.error(`Cevap anahtarı eksik: ${missing.join(', ')}`);
      return;
    }
    const toplamLen = end - start + 1;
    const beklenenLen = fieldOrder.reduce((sum, field) => sum + lengths[field], 0);
    if (toplamLen < beklenenLen) {
      toast.error(`Seçim ${toplamLen} karakter, cevap anahtarı ${beklenenLen} karakter. Lütfen bloğu biraz geniş seçin.`);
      return;
    }
    if (toplamLen > beklenenLen) {
      toast.error(`Seçim ${toplamLen} karakter. Beklenen ${beklenenLen}. Lütfen sadece cevap bloğunu seçin.`);
      return;
    }

    let cursor = start;
    const yeniSecimler = fieldOrder.map((field) => {
      const len = lengths[field];
      const item = { field, start: cursor, end: cursor + len - 1 };
      cursor += len;
      return item;
    });

    setTxtOgrenSelections((prev) => {
      const filtered = prev.filter(item => !fieldOrder.includes(item.field));
      return [...filtered, ...yeniSecimler];
    });
    setTxtOgrenRange({ start: '', end: '' });
    setTxtOgrenPreviewSelection('');
    setTxtOgrenActiveField(TXT_TEMPLATE_BASE_FIELDS[0]);
    toast.success('Cevap bloğu otomatik bölündü.');
  };

  const handleTxtOgrenSave = async () => {
    const selectionMap = txtOgrenSelections.reduce((acc, item) => {
      acc[item.field] = item;
      return acc;
    }, {});
    const requiredFields = ['AdSoyad', 'OgrenciNo', 'Kitapcik', ...txtOgrenDersListesi];
    const missing = requiredFields.filter(field => !selectionMap[field]);
    if (missing.length > 0) {
      toast.error(`Eksik alanlar: ${missing.join(', ')}`);
      return;
    }
    const invalid = txtOgrenSelections.some(item => !item.start || !item.end || item.start > item.end);
    if (invalid) {
      toast.error('Seçim aralıkları geçersiz.');
      return;
    }
    if (txtOgrenSamples.length > 0) {
      const minLineLength = txtOgrenSamples.reduce(
        (min, line) => Math.min(min, String(line || '').length),
        Number.POSITIVE_INFINITY,
      );
      const outOfBounds = txtOgrenSelections.filter((item) => Number(item.end) > minLineLength);
      if (outOfBounds.length > 0) {
        const fields = outOfBounds.map((item) => item.field).join(', ');
        toast.error(`Seçim aralıkları kısa satırları aşıyor (min satır: ${minLineLength}). Düzeltin: ${fields}`);
        return;
      }
    }

    const payload = {
      sablon_id: txtOgrenSablonId ? parseInt(txtOgrenSablonId, 10) : null,
      ad: txtOgrenSablonAd?.trim() || undefined,
      line_mode: txtOgrenLineMode,
      selections: txtOgrenSelections.map(item => ({
        field: item.field,
        start: item.start,
        end: item.end,
      })),
    };

    setTxtOgrenLoading(true);
    setTxtOgrenError(null);
    try {
      const data = await saveTxtSelections(payload);
      const newId = data?.data?.id || data?.id || data?.data?.sablon_id || data?.sablon_id;
      if (newId) {
        setSeciliSablonId(String(newId));
        setOptikSablonlar(prev => {
          const exists = prev.some(item => String(item.id) === String(newId));
          if (exists) return prev;
          return [...prev, { id: newId, ad: payload.ad || `Şablon #${newId}` }];
        });
      }
      toast.success(data?.message || 'TXT şablonu kaydedildi.');
      setShowTxtSablonOgren(false);
      resetTxtOgren();
    } catch (error) {
      const message = error.response?.data?.message || 'TXT şablonu kaydedilemedi.';
      const debug = error.response?.data?.debug || error.response?.data || null;
      setTxtOgrenError({ message, debug });
      toast.error(message);
    } finally {
      setTxtOgrenLoading(false);
    }
  };

  const handleTxtOgrenSaveAndUpload = async () => {
    if (!ensureServerBackedSinav()) return;
    if (txtYukleniyor || txtFlowLoading || txtOgrenLoading) return;
    if (txtOgrenFiles.length === 0) return toast.error('Önce TXT dosyası seçin.');
    if (!txtOgrenCanSave) return toast.error('Önce zorunlu alanları tamamlayın.');

    const selectionMap = txtOgrenSelections.reduce((acc, item) => {
      acc[item.field] = item;
      return acc;
    }, {});
    const requiredFields = ['AdSoyad', 'OgrenciNo', 'Kitapcik', ...txtOgrenDersListesi];
    const missing = requiredFields.filter((field) => !selectionMap[field]);
    if (missing.length > 0) {
      toast.error(`Eksik alanlar: ${missing.join(', ')}`);
      return;
    }

    const templatePayload = {
      sablon_id: txtOgrenSablonId ? parseInt(txtOgrenSablonId, 10) : null,
      ad: txtOgrenSablonAd?.trim() || undefined,
      line_mode: txtOgrenLineMode,
      selections: txtOgrenSelections.map((item) => ({
        field: item.field,
        start: item.start,
        end: item.end,
      })),
    };

    setFlowError(null);
    try {
      const flow = await runSaveAndUpload({
        templatePayload,
        files: txtOgrenFiles,
      });
      const uploadData = flow?.uploadResponse || {};
      const uploadMeta = uploadData?.meta || uploadData?.data?.meta || null;
      const uploadSonuclari = extractDenemeSonuclari(uploadData);
      setTxtUploadMeta(uploadMeta);
      setUploadResult({ sonuclar: uploadSonuclari, meta: uploadMeta });
      if (flow?.sablonId) setSeciliSablonId(String(flow.sablonId));
      if (uploadSonuclari.length > 0) {
        setSonuclar(uploadSonuclari);
        safeSetLocalStorage(`dp_deneme_sonuclari_${seciliSinav}`, JSON.stringify(uploadSonuclari), { clearResultCacheOnQuota: true });
      }
      await loadSonuclarForSinav(seciliSinav);
      toast.success(uploadData?.message || 'Şablon kaydedildi ve sonuçlar yüklendi.');
      setShowTxtSablonOgren(false);
      resetTxtOgren();
      setAktifSekme('karneler');
    } catch (error) {
      const responseData = error.response?.data || null;
      const meta = responseData?.meta || responseData?.data?.meta || null;
      if (meta) {
        setTxtUploadMeta(meta);
        setUploadResult({ sonuclar: [], meta });
      }
      const message = responseData?.message || error.message || 'Kaydet ve yükle akışı başarısız oldu.';
      setFlowError(responseData || message);
      setTxtOgrenError({
        message,
        debug: responseData?.debug || responseData || null,
      });
      toast.error(message);
    }
  };

  const normalizeRow = (row) => normalizeKarneRecord(row);
  const normalizedSonuclar = sonuclar.map(normalizeRow);
  const filtrelenmisSonuclar = normalizedSonuclar.filter((s) => {
    const adSoyad = String(s.adSoyad || `${s.ad || ''} ${s.soyad || ''}`.trim()).toLowerCase();
    const numara = String(s.numara || '');
    return adSoyad.includes(arama.toLowerCase()) || numara.includes(arama);
  });

  const seciliSinavBilgisi = sinavlar.find((sinav) => String(sinav.id) === String(seciliSinav));
  const seciliSinavDetay = seciliSinavBilgisi || null;
  const seciliSinavYerel = Boolean(seciliSinavBilgisi?._localOnly);
  const yuklemeOzeti = {
    toplam: sonuclar.length,
    kayitli: sonuclar.filter((item) => item.tip === 'kayitli').length,
    misafir: sonuclar.filter((item) => item.tip !== 'kayitli').length,
  };

  const sinavlarWithSummary = sinavlar.map((sinav) => {
    const cached = getCachedSonuclar(sinav.id);
    const sonucSayisi = Math.max(Number(sinav.katilimci_sayisi || 0), cached.length);
    const ortalamaNet = sonucSayisi > 0 ? (cached.length > 0 ? (cached.reduce((toplam, item) => toplam + Number(item.toplamNet || 0), 0) / cached.length) : Number(sinav.ortalama_net || 0)) : null;
    return { ...sinav, sonuc_sayisi: sonucSayisi, ortalama_net_ozet: ortalamaNet };
  });

  const hesaplaSinavOrtalamalari = (ogrenciListesi = []) => {
    const dersOzet = {};
    ogrenciListesi.forEach((ogr) => {
      (ogr?.detaylar || []).forEach((item) => {
        const ders = item?.ders || 'Ders';
        const net = Number(item?.net || 0);
        if (!dersOzet[ders]) dersOzet[ders] = { toplamNet: 0, adet: 0 };
        dersOzet[ders].toplamNet += net;
        dersOzet[ders].adet += 1;
      });
    });
    const sonuc = {};
    Object.keys(dersOzet).forEach((ders) => {
      const toplamNet = dersOzet[ders].toplamNet;
      const adet = Math.max(1, dersOzet[ders].adet);
      sonuc[ders] = { toplamNet, ortalamaNet: toplamNet / adet };
    });
    return sonuc;
  };

  const normalizeDersKey = (raw = '') => normalizeLessonToken(raw);
  const dersKeyFromItem = (ders) => {
    const n = normalizeDersKey(ders);
    if (n.includes('turk')) return 'turkce';
    if (n.includes('sosyal')) return 'sosyal';
    if (n.includes('mat')) return 'mat';
    if (n.includes('fen')) return 'fen';
    return null;
  };

  const buildSubjectStats = (ogrenci) => {
    const stats = { turkce: { d: 0, y: 0, n: 0 }, sosyal: { d: 0, y: 0, n: 0 }, mat: { d: 0, y: 0, n: 0 }, fen: { d: 0, y: 0, n: 0 } };
    (ogrenci?.detaylar || []).forEach((item) => {
      const key = dersKeyFromItem(item?.ders);
      if (!key) return;
      stats[key].d += Number(item?.dogru || 0);
      stats[key].y += Number(item?.yanlis || 0);
      stats[key].n += Number(item?.net || 0);
    });
    const toplam = Object.values(stats).reduce((acc, val) => {
      acc.d += val.d; acc.y += val.y; acc.n += val.n; return acc;
    }, { d: 0, y: 0, n: 0 });
    return { stats, toplam };
  };

  const buildAverageStats = (ogrenciListesi = []) => {
    const toplam = { turkce: { d: 0, y: 0, n: 0 }, sosyal: { d: 0, y: 0, n: 0 }, mat: { d: 0, y: 0, n: 0 }, fen: { d: 0, y: 0, n: 0 } };
    const adet = Math.max(1, ogrenciListesi.length);
    ogrenciListesi.forEach((ogr) => {
      const { stats } = buildSubjectStats(ogr);
      Object.keys(toplam).forEach((k) => {
        toplam[k].d += stats[k].d; toplam[k].y += stats[k].y; toplam[k].n += stats[k].n;
      });
    });
    const ort = {};
    Object.keys(toplam).forEach((k) => { ort[k] = { d: toplam[k].d / adet, y: toplam[k].y / adet, n: toplam[k].n / adet }; });
    const toplamOrt = Object.values(ort).reduce((acc, val) => {
      acc.d += val.d; acc.y += val.y; acc.n += val.n; return acc;
    }, { d: 0, y: 0, n: 0 });
    return { ort, toplamOrt };
  };

  // KUSURSUZ HİZALANMIŞ PDF OLUŞTURUCU
const handleTopluPdfIndir = async () => {
    if (filtrelenmisSonuclar.length === 0) return toast.error('PDF için öğrenci bulunamadı.');

    setPdfOlusturuluyor(true);
    setPdfProgress('Genel liste hazırlanıyor...');
    const ortalamalar = hesaplaSinavOrtalamalari(sonuclar);
    const MAX_OGRENCI_PER_DOSYA = 90;
    const CANVAS_SCALE = 1.35;
    const JPEG_QUALITY = 0.82;
    const cokluDosya = filtrelenmisSonuclar.length > MAX_OGRENCI_PER_DOSYA;
    let dosyaIndex = 1;
    let doc = new jsPDF('p', 'mm', 'a4');
    let currentDocStudentCount = 0;
    let currentDocHasSummaryPage = true;

    try {
      const sinavAdi = seciliSinavBilgisi?.ad || 'Deneme';
      const baseFileName = `Karneler_${normalizeTR(sinavAdi)}`;
      const sinavTarihi = seciliSinavBilgisi?.tarih
        ? new Date(seciliSinavBilgisi.tarih).toLocaleDateString('tr-TR')
        : new Date().toLocaleDateString('tr-TR');

      doc.setFontSize(12);
      doc.text(normalizeTR('OKUL TEST BAZLI NET LİSTESİ (TYT sıralı)'), 5, 12);
      doc.setFontSize(10);
      doc.text(normalizeTR(`${sinavAdi} • ${sinavTarihi}`), 5, 17);

      const { ort, toplamOrt } = buildAverageStats(filtrelenmisSonuclar);
      const format3 = (v) => Number(v || 0).toFixed(2);

      const ortalamaRow = [
        '', '', 'Genel Ortalama', '',
        format3(ort.turkce.d), format3(ort.turkce.y), format3(ort.turkce.n),
        format3(ort.sosyal.d), format3(ort.sosyal.y), format3(ort.sosyal.n),
        format3(ort.mat.d), format3(ort.mat.y), format3(ort.mat.n),
        format3(ort.fen.d), format3(ort.fen.y), format3(ort.fen.n),
        format3(toplamOrt.d), format3(toplamOrt.y), format3(toplamOrt.n),
        '',
      ];

      const okulOrtalamaRow = [
        '', '', 'Okul Ortalamasi', '',
        format3(ort.turkce.d), format3(ort.turkce.y), format3(ort.turkce.n),
        format3(ort.sosyal.d), format3(ort.sosyal.y), format3(ort.sosyal.n),
        format3(ort.mat.d), format3(ort.mat.y), format3(ort.mat.n),
        format3(ort.fen.d), format3(ort.fen.y), format3(ort.fen.n),
        format3(toplamOrt.d), format3(toplamOrt.y), format3(toplamOrt.n),
        '',
      ];

      const tabloBody = [
        ortalamaRow,
        okulOrtalamaRow,
        ...filtrelenmisSonuclar.map((s, idx) => {
          const { stats, toplam } = buildSubjectStats(s);
          return ([
            s.siralama ?? (idx + 1),
            s.numara || '-',
            normalizeTR(s.adSoyad || `${s.ad || ''} ${s.soyad || ''}`.trim()),
            normalizeTR(s.sinif || '-'),
            format3(stats.turkce.d), format3(stats.turkce.y), format3(stats.turkce.n),
            format3(stats.sosyal.d), format3(stats.sosyal.y), format3(stats.sosyal.n),
            format3(stats.mat.d), format3(stats.mat.y), format3(stats.mat.n),
            format3(stats.fen.d), format3(stats.fen.y), format3(stats.fen.n),
            format3(toplam.d), format3(toplam.y), format3(toplam.n),
            s.puan ?? '-',
          ]);
        }),
      ];

      autoTable(doc, {
        startY: 22,
        margin: { left: 5, right: 5 },
        tableWidth: 200,
        head: [
          [
            { content: 'Sira', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'O.No', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'Isim', rowSpan: 2, styles: { halign: 'left', valign: 'middle' } },
            { content: 'Sinif', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
            { content: 'TURKCE', colSpan: 3, styles: { halign: 'center' } },
            { content: 'SOSYAL', colSpan: 3, styles: { halign: 'center' } },
            { content: 'MATEMATIK', colSpan: 3, styles: { halign: 'center' } },
            { content: 'FEN', colSpan: 3, styles: { halign: 'center' } },
            { content: 'TOPLAM', colSpan: 3, styles: { halign: 'center' } },
            { content: 'PUAN', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
          ],
          [
            { content: 'D', styles: { halign: 'center' } }, { content: 'Y', styles: { halign: 'center' } }, { content: 'N', styles: { halign: 'center' } },
            { content: 'D', styles: { halign: 'center' } }, { content: 'Y', styles: { halign: 'center' } }, { content: 'N', styles: { halign: 'center' } },
            { content: 'D', styles: { halign: 'center' } }, { content: 'Y', styles: { halign: 'center' } }, { content: 'N', styles: { halign: 'center' } },
            { content: 'D', styles: { halign: 'center' } }, { content: 'Y', styles: { halign: 'center' } }, { content: 'N', styles: { halign: 'center' } },
            { content: 'D', styles: { halign: 'center' } }, { content: 'Y', styles: { halign: 'center' } }, { content: 'N', styles: { halign: 'center' } },
          ],
        ],
        body: tabloBody,
        theme: 'grid',
        styles: { 
          fontSize: 6.2, 
          cellPadding: { top: 0.6, right: 0.15, bottom: 0.6, left: 0.15 },
          overflow: 'hidden',
          halign: 'center',
          valign: 'middle',
          lineColor: [100, 100, 100], 
          lineWidth: 0.1, 
          textColor: [0, 0, 0] 
        },
        headStyles: { 
          fillColor: [230, 230, 230],
          textColor: [0, 0, 0], 
          fontStyle: 'bold' 
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 7 },   // Sıra
          1: { halign: 'center', cellWidth: 13 },  // Ö.No
          2: { halign: 'left',   cellWidth: 46 },  // İsim
          3: { halign: 'center', cellWidth: 10 },  // Sınıf
          
          4:  { halign: 'center', cellWidth: 7 }, 5:  { halign: 'center', cellWidth: 7 }, 6:  { halign: 'center', cellWidth: 8, fontStyle: 'bold' }, // Türkçe
          7:  { halign: 'center', cellWidth: 7 }, 8:  { halign: 'center', cellWidth: 7 }, 9:  { halign: 'center', cellWidth: 8, fontStyle: 'bold' }, // Sosyal
          10: { halign: 'center', cellWidth: 7 }, 11: { halign: 'center', cellWidth: 7 }, 12: { halign: 'center', cellWidth: 8, fontStyle: 'bold' }, // Matematik
          13: { halign: 'center', cellWidth: 7 }, 14: { halign: 'center', cellWidth: 7 }, 15: { halign: 'center', cellWidth: 8, fontStyle: 'bold' }, // Fen
          
          16: { halign: 'center', cellWidth: 7 }, 17: { halign: 'center', cellWidth: 7 }, 18: { halign: 'center', cellWidth: 8, fontStyle: 'bold', fillColor: [245, 245, 245] }, // Toplam
          19: { halign: 'center', cellWidth: 14, fontStyle: 'bold' }, // Puan
        },
        didParseCell: (data) => {
          if (data.section === 'body') {
            if (data.row.index === 0 || data.row.index === 1) {
              data.cell.styles.fillColor = [235, 245, 255];
              data.cell.styles.fontStyle = 'bold';
            } else if (data.row.index % 2 === 1) {
              data.cell.styles.fillColor = [249, 250, 251]; 
            }
          }
        },
      });

      let yCursor = (doc.lastAutoTable?.finalY || 20) + 6;
      doc.setFontSize(9);
      Object.entries(ortalamalar).forEach(([ders, info]) => {
        if (yCursor > 280) { doc.addPage(); yCursor = 14; }
        doc.text(normalizeTR(`Sinav ${ders} Ortalamasi: ${info.ortalamaNet.toFixed(2)} Net`), 5, yCursor);
        yCursor += 5;
      });

      for (let i = 0; i < filtrelenmisSonuclar.length; i += 1) {
        if (currentDocStudentCount >= MAX_OGRENCI_PER_DOSYA) {
          const partName = `${baseFileName}_Part${dosyaIndex}.pdf`;
          doc.save(partName);
          dosyaIndex += 1;
          doc = new jsPDF('p', 'mm', 'a4');
          currentDocStudentCount = 0;
          currentDocHasSummaryPage = false;
        }

        setAktifPrintOgrenci(filtrelenmisSonuclar[i]);
        setPdfProgress(`Bireysel Karneler Çiziliyor: ${i + 1} / ${filtrelenmisSonuclar.length}`);
        
        await new Promise(r => requestAnimationFrame(r));
        await new Promise(r => setTimeout(r, i % 5 === 0 ? 40 : 10));

        const target = document.getElementById('gizli-karne-alani');
        if (!target) continue;

        const canvas = await html2canvas(target, { scale: CANVAS_SCALE, useCORS: true, backgroundColor: '#ffffff', logging: false });
        
        const imgData = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const renderHeight = Math.min(imgHeight, pageHeight);

        if (currentDocHasSummaryPage || currentDocStudentCount > 0) doc.addPage();
        doc.addImage(imgData, 'JPEG', 0, 0, imgWidth, renderHeight);

        canvas.width = 1;
        canvas.height = 1;
        currentDocStudentCount += 1;
        setAktifPrintOgrenci(null);
      }

      const finalFileName = cokluDosya ? `${baseFileName}_Part${dosyaIndex}.pdf` : `${baseFileName}.pdf`;
      doc.save(finalFileName);
      toast.success(cokluDosya ? `PDF tamamlandı (${dosyaIndex} parça).` : 'Kapsamlı Sınav Raporu Hazırlandı!');
    } catch (error) {
      toast.error('PDF hazırlanırken hata oluştu.');
    } finally {
      setPdfOlusturuluyor(false);
      setPdfProgress('');
      setAktifPrintOgrenci(null);
    }
  };

  const containerStyle = { display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 20, padding: isMobile ? '10px' : '0', width: '100%', maxWidth: '100vw', overflowX: 'hidden' };
  const sectionHeaderStyle = { fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: isMobile ? '1.1rem' : '1.4rem', lineHeight: 1.3 };
  const tabContainerStyle = { display: 'flex', background: 'var(--bg-elevated)', padding: isMobile ? 4 : 6, borderRadius: 12, gap: isMobile ? 4 : 8, flexWrap: isMobile ? 'wrap' : 'nowrap', flexDirection: isMobile ? 'column' : 'row' };
  const tabButtonStyle = (isActive, activeColor) => ({ flex: 1, justifyContent: 'center', padding: isMobile ? '10px 8px' : '10px 16px', fontSize: isMobile ? '0.85rem' : 'inherit', minWidth: isMobile ? '100%' : 'auto', background: isActive ? activeColor : 'transparent', color: isActive ? 'white' : 'inherit', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s ease' });
  const cardStyle = { background: 'var(--bg-card, white)', borderRadius: 12, padding: isMobile ? 16 : 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', width: '100%', boxSizing: 'border-box' };
  const tableWrapperStyle = { overflowX: isMobile ? 'hidden' : 'auto', width: '100%', WebkitOverflowScrolling: 'touch' };
  const tableStyle = { width: '100%', minWidth: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '0.76rem' : '0.9rem', tableLayout: isMobile ? 'fixed' : 'auto' };
  const grid2ColStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 8 : 12 };
  const grid3ColStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: isMobile ? 8 : 12 };
  const flexBetweenStyle = { display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 0, marginBottom: isMobile ? 16 : 20 };
  const formGroupStyle = { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: isMobile ? 12 : 16 };
  const inputStyle = { padding: isMobile ? '10px 12px' : '10px 14px', borderRadius: 8, border: '1px solid var(--border, #E2E8F0)', fontSize: '16px', width: '100%', boxSizing: 'border-box', appearance: 'none', WebkitAppearance: 'none' };
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  const dropZoneStyle = (isActive = false) => ({ border: `2px dashed ${isActive ? '#E11D48' : 'rgba(29, 78, 216, 0.22)'}`, borderRadius: 16, padding: isMobile ? '22px 16px' : '24px 20px', background: isActive ? 'rgba(225, 29, 72, 0.06)' : 'rgba(248, 250, 252, 0.9)', cursor: 'pointer', transition: 'all 0.2s ease', textAlign: 'center' });
  const modalBackdropStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? '0' : '20px' };
  const modalStyle = { background: 'white', borderRadius: isMobile ? '20px 20px 0 0' : 20, padding: isMobile ? '20px' : 0, width: isMobile ? '100%' : 'min(1120px, calc(100vw - 32px))', maxWidth: '100vw', maxHeight: isMobile ? '92vh' : 'calc(100vh - 32px)', overflowY: 'auto', position: 'relative', margin: 0 };
  const karneHeaderStyle = { background: '#0D1B3A', color: 'white', padding: isMobile ? '16px 16px 20px' : '20px 24px 24px', position: 'relative' };
  const karneProfileGrid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.5fr) minmax(320px, 0.9fr)', alignItems: isMobile ? 'center' : 'start', gap: isMobile ? 16 : 20 };
  const avatarStyle = { width: isMobile ? 56 : 72, height: isMobile ? 56 : 72, borderRadius: '50%', background: 'linear-gradient(145deg, #527BFF, #3356D8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: isMobile ? '1.2rem' : '1.45rem', fontWeight: 900, boxShadow: '0 10px 24px rgba(52,86,216,0.18)', border: '4px solid #E8EDF8', flexShrink: 0 };
  const statCardStyle = { background: 'white', border: '1px solid #E2E8F0', borderRadius: 18, padding: isMobile ? '12px 14px' : '14px 16px', minWidth: 0, textAlign: 'center', boxShadow: '0 8px 20px rgba(15,23,42,0.04)' };
  const rankCardStyle = { background: 'linear-gradient(180deg, #FBBF24, #F97316)', borderRadius: 18, padding: isMobile ? '12px 14px' : '14px 16px', minWidth: 0, textAlign: 'center', color: 'white', boxShadow: '0 12px 24px rgba(249,115,22,0.22)' };

  const getCleanLessonLabel = (rawDers = '') => {
    const label = String(rawDers || '').trim();
    const normalized = label.toLowerCase('tr-TR');
    if (normalized.includes('turk')) return 'Türkçe';
    if (normalized.includes('sosyal')) return 'Sosyal B.';
    if (normalized.includes('mat')) return 'Matematik';
    if (normalized.includes('fen')) return 'Fen B.';
    if (normalized.includes('geo')) return 'Geometri';
    return label.length > 36 ? `${label.slice(0, 36)}...` : label;
  };

  const getLessonIcon = (ders = '') => {
    const normalized = String(ders || '').toLowerCase('tr-TR');
    if (normalized.includes('turk')) return '📘';
    if (normalized.includes('sosyal')) return '🌍';
    if (normalized.includes('mat')) return '🧮';
    if (normalized.includes('fen')) return '🧪';
    if (normalized.includes('din')) return '🕌';
    if (normalized.includes('dil')) return '💬';
    return '📚';
  };

  const getSablonLabel = (sablon) => {
    if (!sablon) return '';
    return sablon.ad || sablon.adi || sablon.name || `Şablon #${sablon.id}`;
  };
  const getSonucRowKey = (sonuc, index) => [
    sonuc?.id, sonuc?.ogrenci_id, sonuc?.numara, sonuc?.ad, sonuc?.soyad, sonuc?.tip, sonuc?.siralama, index,
  ].filter((item) => item !== undefined && item !== null && item !== '').join('_');

  const optikAdimKartStyle = (renk) => ({
    ...cardStyle, padding: isMobile ? 16 : 20, border: `1px solid ${renk}`, boxShadow: '0 10px 24px rgba(15,23,42,0.05)',
  });

  const txtOgrenSample = txtOgrenSamples[txtOgrenSelectedSample] || '';
  const txtOgrenExpectedLength = (() => {
    const aktifCevapAnahtari = cevapAnahtari?.[aktifKitapcik] || {};
    return txtOgrenDersListesi.reduce((sum, ders) => {
      const raw = String(aktifCevapAnahtari?.[ders] || '');
      return sum + raw.replace(/\s+/g, '').length;
    }, 0);
  })();
  const txtOgrenSelectionMap = txtOgrenSelections.reduce((acc, item) => {
    acc[item.field] = item;
    return acc;
  }, {});
  const txtOgrenBaseRequired = ['AdSoyad', 'OgrenciNo', 'Kitapcik'];
  const txtOgrenRequiredFields = [...txtOgrenBaseRequired, ...txtOgrenDersListesi];
  const txtOgrenMissingFields = txtOgrenRequiredFields.filter(field => !txtOgrenSelectionMap[field]);
  const txtOgrenInvalidRange = txtOgrenSelections.some(item => !item.start || !item.end || Number(item.start) > Number(item.end));
  const txtOgrenCanSave = txtOgrenMissingFields.length === 0 && !txtOgrenInvalidRange;
  const activeUploadMeta = uploadResult?.meta || txtUploadMeta;

  const normalizeKazanimSablonu = (raw = {}) => {
    const toLessonMap = (obj = {}) => {
      const next = {};
      Object.keys(obj || {}).forEach((ders) => {
        const key = normalizeDersAdi(ders);
        const list = Array.isArray(obj[ders]) ? obj[ders] : [];
        next[key] = list.map((item) => ({
          start: String(item?.start ?? ''),
          end: String(item?.end ?? ''),
          konu: String(item?.konu || ''),
        }));
      });
      return next;
    };
    const kitapcikAdaylari = Object.keys(raw || {}).filter((key) => /^[A-Z]$/i.test(String(key || '')));
    const kitapcikBazli =
      kitapcikAdaylari.length > 0
      && kitapcikAdaylari.every((key) => raw?.[key] && typeof raw[key] === 'object' && !Array.isArray(raw[key]));
    if (kitapcikBazli) {
      const byBooklet = {};
      kitapcikAdaylari.forEach((k) => {
        byBooklet[String(k).toUpperCase()] = toLessonMap(raw[k]);
      });
      return byBooklet;
    }
    return toLessonMap(raw);
  };

  const ensureKazanimForLessons = (base = {}, dersler = [], kitapciklar = kitapciklarListesi) => {
    const keys = Array.isArray(kitapciklar) && kitapciklar.length > 0 ? kitapciklar : ['A', 'B'];
    const looksBookletScoped = Object.keys(base || {}).some((k) => /^[A-Z]$/i.test(String(k || '')));
    const next = {};
    keys.forEach((booklet) => {
      const b = String(booklet || '').toUpperCase();
      const source = looksBookletScoped ? (base?.[b] || {}) : (base || {});
      next[b] = { ...(source || {}) };
      dersler.forEach((ders) => {
        const key = normalizeDersAdi(ders);
        if (!Array.isArray(next[b][key])) next[b][key] = [];
      });
    });
    return next;
  };

  const getAktifKazanimMap = (booklet = aktifKitapcik) => {
    const b = String(booklet || aktifKitapcik || 'A').toUpperCase();
    const scoped = kazanimSablonu?.[b];
    if (scoped && typeof scoped === 'object' && !Array.isArray(scoped)) return scoped;
    return {};
  };

  const loadCevapAnahtariVeKazanim = async (denemeId) => {
    if (!denemeId) return;
    try {
      const { data } = await axios.get(`/denemeler/${denemeId}/cevap-anahtari`);
      const payload = data?.data || {};
      const ca = payload?.cevap_anahtari || {};
      const tur = ca?.tur || 'TYT';
      const kitapciklar = ca?.kitapciklar && typeof ca.kitapciklar === 'object' ? ca.kitapciklar : {};
      const kitapcikKeys = Object.keys(kitapciklar).length > 0 ? Object.keys(kitapciklar) : ['A', 'B'];
      const normalizedKitapciklar = {};
      kitapcikKeys.forEach((k) => {
        const mevcut = kitapciklar[k] || {};
        normalizedKitapciklar[k] = {};
        Object.keys(mevcut).forEach((ders) => {
          normalizedKitapciklar[k][normalizeDersAdi(ders)] = normalizeAnswerChars(mevcut[ders]);
        });
      });
      setSinavTuru(tur);
      setKitapciklarListesi(kitapcikKeys);
      setAktifKitapcik((prev) => (kitapcikKeys.includes(prev) ? prev : kitapcikKeys[0]));
      setCevapAnahtari(normalizedKitapciklar);
      const dersListesi = uniqLessonLabels([
        ...(EXAM_SUBJECTS[tur] || []),
        ...kitapcikKeys.flatMap((k) => Object.keys(normalizedKitapciklar[k] || {})),
      ]);
      setTxtOgrenDersListesi(dersListesi);

      const ks = payload?.kazanim_sablonu?.dersler || {};
      const normalized = normalizeKazanimSablonu(ks);
      setKazanimSablonu(ensureKazanimForLessons(normalized, dersListesi, kitapcikKeys));
    } catch (error) {
      const dersler = Array.from(new Set([...(EXAM_SUBJECTS[sinavTuru] || []), ...txtOgrenDersListesi]));
      setKazanimSablonu((prev) => ensureKazanimForLessons(prev, dersler, kitapciklarListesi));
      throw error;
    }
  };

  useEffect(() => {
    if (!seciliSinav) return;
    loadCevapAnahtariVeKazanim(seciliSinav).catch(() => {});
  }, [seciliSinav]);

  useEffect(() => {
    const start = parseInt(txtOgrenRange.start, 10);
    const end = parseInt(txtOgrenRange.end, 10);
    if (!start || !end || start > end) {
      if (txtOgrenPreviewSelection) setTxtOgrenPreviewSelection('');
      return;
    }
    const line = txtOgrenSamples[txtOgrenSelectedSample] || '';
    setTxtOgrenPreviewSelection(line.slice(start - 1, end));
  }, [txtOgrenRange.start, txtOgrenRange.end, txtOgrenSelectedSample, txtOgrenSamples, txtOgrenPreviewSelection]);

  const ensureServerBackedSinav = () => {
    if (!seciliSinav) { toast.error('Önce bir deneme seçin.'); return false; }
    if (seciliSinavYerel) { toast.error('Bu deneme sadece tarayıcıda kayıtlı. Optik yükleme için backendde oluşturulmuş gerçek bir deneme seçin.'); return false; }
    return true;
  };

  const getDersOtomatikAralik = (ders) => {
    const normalized = normalizeDersAdi(ders);
    const getAnswerLengthForLesson = (lessonName) => {
      const kitapcikData = cevapAnahtari?.[aktifKitapcik] || {};
      const direct = String(kitapcikData?.[lessonName] || '').replace(/\s+/g, '').length;
      if (direct > 0) return direct;
      const matchKey = Object.keys(kitapcikData).find(
        (key) => normalizeLessonToken(key) === normalizeLessonToken(lessonName),
      );
      return String(kitapcikData?.[matchKey] || '').replace(/\s+/g, '').length;
    };
    const len = getAnswerLengthForLesson(normalized);
    if (len <= 0) return { start: 1, end: 0, len: 0 };
    return { start: 1, end: len, len };
  };

  const getKazanimMetni = (ders) => {
    const key = normalizeDersAdi(ders);
    const aktifMap = getAktifKazanimMap();
    const rows = Array.isArray(aktifMap[key]) ? aktifMap[key] : [];
    return rows.map((r) => String(r.konu || '')).join('\n');
  };

  const handleKazanimMetniDegis = (ders, text) => {
    const key = normalizeDersAdi(ders);
    const { start, end, len } = getDersOtomatikAralik(key);
    if (len <= 0 || end < start) {
      setKazanimSablonu((prev) => {
        const b = String(aktifKitapcik || 'A').toUpperCase();
        const prevBooklet = prev?.[b] && typeof prev[b] === 'object' && !Array.isArray(prev[b]) ? prev[b] : {};
        return {
          ...prev,
          [b]: {
            ...prevBooklet,
            [key]: [],
          },
        };
      });
      return;
    }
    const lines = String(text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    const max = Math.min(lines.length, len);
    const nextRows = Array.from({ length: max }).map((_, idx) => ({
      start: String(start + idx),
      end: String(start + idx),
      konu: lines[idx],
    }));
    setKazanimSablonu((prev) => {
      const b = String(aktifKitapcik || 'A').toUpperCase();
      const prevBooklet = prev?.[b] && typeof prev[b] === 'object' && !Array.isArray(prev[b]) ? prev[b] : {};
      return {
        ...prev,
        [b]: {
          ...prevBooklet,
          [key]: nextRows,
        },
      };
    });
  };

  const buildKonuAnalizFromSablon = (student) => {
    if (!student || Object.keys(kazanimSablonu).length === 0) return {};
    const kitapcik = String(student?.kitapcik || aktifKitapcik || 'A').toUpperCase();
    const kitapcikAnahtari = cevapAnahtari?.[kitapcik] || {};
    const kazanımDersleri = getAktifKazanimMap(kitapcik);
    const konuAnaliz = {};

    Object.entries(kazanımDersleri).forEach(([ders, ranges]) => {
      const dogruAnahtar = normalizeAnswerChars(kitapcikAnahtari[ders] || kitapcikAnahtari[normalizeDersAdi(ders)] || '');
      const ogrenciCevap = tryFindStudentLessonAnswers(student, ders);
      if (!dogruAnahtar || !ogrenciCevap) return;
      const dersUzunluk = Math.min(dogruAnahtar.length, ogrenciCevap.length);
      const rawRanges = (Array.isArray(ranges) ? ranges : [])
        .map((range) => ({
          ...range,
          start: Number(range?.start || 0),
          end: Number(range?.end || 0),
        }))
        .filter((range) => range.start > 0 && range.end > 0 && range.end >= range.start);
      if (rawRanges.length === 0) return;

      // Eski/global kayıtlar (örn: Matematik 66-105) gelirse, ders bazlı local aralığa çevir.
      const minStart = Math.min(...rawRanges.map((r) => r.start));
      const hepsiGlobal = minStart > dersUzunluk && dersUzunluk > 0;
      const normalizedRanges = hepsiGlobal
        ? rawRanges.map((r) => ({ ...r, start: r.start - (minStart - 1), end: r.end - (minStart - 1) }))
        : rawRanges;

      const dersSonuc = normalizedRanges.map((range) => {
        let dogru = 0;
        let yanlis = 0;
        const start = Number(range.start);
        const end = Number(range.end);
        if (!start || !end || end < start) return null;
        for (let i = start; i <= end; i += 1) {
          const key = dogruAnahtar[i - 1] || '';
          const ans = ogrenciCevap[i - 1] || '';
          if (!ans || ans === '*' || ans === 'X') continue;
          if (ans === key) dogru += 1;
          else yanlis += 1;
        }
        const soru = Math.max(0, end - start + 1);
        const basari = soru > 0 ? Math.round((dogru / soru) * 100) : 0;
        return { konu: range.konu, soru, dogru, yanlis, basari };
      }).filter(Boolean);
      if (dersSonuc.length > 0) konuAnaliz[ders] = dersSonuc;
    });

    return konuAnaliz;
  };

  const buildLessonStatsFromAnswerKey = (student) => {
    if (!student) return {};
    const kitapcik = String(student?.kitapcik || aktifKitapcik || 'A').toUpperCase();
    const kitapcikAnahtari = cevapAnahtari?.[kitapcik] || {};
    const result = {};

    KARNE_CORE_DERSLER.forEach((ders) => {
      const anahtar = normalizeAnswerChars(
        kitapcikAnahtari[ders]
        || kitapcikAnahtari[normalizeDersAdi(ders)]
        || ''
      );
      const cevap = tryFindStudentLessonAnswers(student, ders);
      if (!anahtar || !cevap) return;

      const len = Math.min(anahtar.length, cevap.length);
      let dogru = 0;
      let yanlis = 0;
      for (let i = 0; i < len; i += 1) {
        const key = anahtar[i] || '';
        const ans = cevap[i] || '';
        if (!ans || ans === '*' || ans === 'X') continue;
        if (ans === key) dogru += 1;
        else yanlis += 1;
      }

      result[normalizeLessonToken(ders)] = { dogru, yanlis, soru: len };
    });

    return result;
  };

  const buildDisplayLessonStats = (student, fixedDetaylar = []) => {
    const detailStats = {};
    (fixedDetaylar || []).forEach((item) => {
      const key = normalizeLessonToken(item?.ders);
      if (!key) return;
      const nextItem = {
        ders: item?.ders || '',
        soru: Number(item?.soru || 0),
        dogru: Number(item?.dogru || 0),
        yanlis: Number(item?.yanlis || 0),
        bos: Number(item?.bos || 0),
        net: Number(item?.net || 0),
        source: 'detail',
      };
      const prevItem = detailStats[key];
      if (!prevItem) {
        detailStats[key] = nextItem;
        return;
      }

      const prevFilled = Number(prevItem.dogru || 0) + Number(prevItem.yanlis || 0) + Number(prevItem.bos || 0);
      const nextFilled = Number(nextItem.dogru || 0) + Number(nextItem.yanlis || 0) + Number(nextItem.bos || 0);
      const prevSoru = Number(prevItem.soru || 0);
      const nextSoru = Number(nextItem.soru || 0);

      if (nextFilled > prevFilled || (nextFilled === prevFilled && nextSoru > prevSoru)) {
        detailStats[key] = nextItem;
      }
    });

    const derivedStats = buildLessonStatsFromAnswerKey(student);
    const merged = {};

    KARNE_CORE_DERSLER.forEach((ders) => {
      const key = normalizeLessonToken(ders);
      const detail = detailStats[key] || {
        ders,
        soru: 0,
        dogru: 0,
        yanlis: 0,
        bos: 0,
        net: 0,
        source: 'detail',
      };
      const derived = derivedStats[key];

      if (derived && Number(derived.soru || 0) > 0) {
        const soru = Number(derived.soru || 0);
        const dogru = Number(derived.dogru || 0);
        const yanlis = Number(derived.yanlis || 0);
        const bos = Math.max(0, soru - dogru - yanlis);
        merged[key] = {
          ders,
          soru,
          dogru,
          yanlis,
          bos,
          net: dogru - (yanlis / 4),
          source: 'derived',
        };
        return;
      }

      merged[key] = {
        ...detail,
        ders,
        source: 'detail',
      };
    });

    const totalNetTarget = Number(student?.toplamNet || 0);
    const detailNetTotal = Object.values(detailStats).reduce((sum, item) => sum + Number(item?.net || 0), 0);
    const mergedNetTotal = Object.values(merged).reduce((sum, item) => sum + Number(item?.net || 0), 0);
    const detailDiff = Math.abs(detailNetTotal - totalNetTarget);
    const mergedDiff = Math.abs(mergedNetTotal - totalNetTarget);

    return mergedDiff <= detailDiff ? merged : detailStats;
  };

  return (
    <div style={containerStyle}>
      <style>{`
        @page { size: A4; margin: 0; }
        .print-root { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @media print {
          body * { visibility: hidden; }
          #karne-print-area, #karne-print-area * { visibility: visible; }
          #karne-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
        @media (max-width: 768px) {
          .responsive-table { font-size: 0.75rem; }
          .responsive-table th, .responsive-table td { padding: 8px 6px; }
          .hide-mobile { display: none !important; }
          .hide-mobile-table { display: none !important; }
        }
        .kitapcik-tab { padding: 8px 16px; border-radius: 8px; cursor: pointer; font-weight: 700; transition: all 0.2s; }
        .kitapcik-tab.active { background: #E11D48; color: white; }
        .kitapcik-tab.inactive { background: rgba(225, 29, 72, 0.1); color: #E11D48; }
      `}</style>

      <div className="section-header">
        <div>
          <h2 style={sectionHeaderStyle}>📊 Optik Okuma & Karne Sistemi</h2>
          <div className="section-subtitle" style={{ fontSize: isMobile ? '0.8rem' : 'inherit' }}>Sınav tanımlayın, dosyayı yükleyin, detaylı karneleri oluşturun.</div>
        </div>
      </div>

      <div className="no-print" style={tabContainerStyle}>
        <button style={tabButtonStyle(aktifSekme === 'list', 'var(--primary, #3B82F6)')} onClick={() => setAktifSekme('list')}>📋 Sınavlar</button>
        <button style={tabButtonStyle(aktifSekme === 'yukle', '#10B981')} onClick={() => setAktifSekme('yukle')}>📤 Optik Yükle</button>
        <button style={tabButtonStyle(aktifSekme === 'karneler', 'linear-gradient(45deg, #3B82F6, #8B5CF6)')} onClick={() => setAktifSekme('karneler')}>📑 Karneler</button>
      </div>

      {aktifSekme === 'list' && (
        <div className="card animate-fadeInUp" style={cardStyle}>
          <div style={flexBetweenStyle}>
            <h3 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>Sistemdeki Sınavlar</h3>
            <button className="btn btn-primary" onClick={() => setShowYeniSinav(true)} style={{ padding: isMobile ? '10px 14px' : '10px 20px', fontSize: isMobile ? '0.85rem' : 'inherit', whiteSpace: 'nowrap' }}>+ Yeni Sınav</button>
          </div>
          {sinavlar.length === 0 ? (
            <div className="empty-state" style={{ padding: isMobile ? 30 : 40 }}><p style={{ fontSize: isMobile ? '0.9rem' : 'inherit' }}>Henüz sınav tanımlanmamış.</p></div>
          ) : (
            <div style={tableWrapperStyle}>
              <table className="table responsive-table" style={tableStyle}>
                <thead><tr><th>Sınav Adı</th><th className="hide-mobile-table">Tarih</th><th className="hide-mobile-table">Türü</th><th>Sonuçlar</th></tr></thead>
                <tbody>
                  {sinavlarWithSummary.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 700 }}>{s.ad}</td>
                      <td className="hide-mobile-table">{new Date(s.tarih).toLocaleDateString('tr-TR')}</td>
                      <td className="hide-mobile-table"><span className="badge badge-info">{s.tur}</span></td>
                      <td>
                        {Number(s.sonuc_sayisi || 0) > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#2563EB' }}>#{s.sonuc_sayisi} sonuç</span>
                              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Ort. Net: {Number(s.ortalama_net_ozet || 0).toFixed(1)}</span>
                            </div>
                            <button className="btn btn-outline btn-sm" style={{ padding: isMobile ? '8px 12px' : '6px 12px', fontSize: isMobile ? '0.8rem' : '0.875rem' }} onClick={() => { setSeciliSinav(s.id); setAktifSekme('yukle'); }}>⚙️ Optik Akışı</button>
                            <button className="btn btn-primary btn-sm" style={{ background: 'linear-gradient(45deg, #3B82F6, #8B5CF6)', border: 'none', padding: isMobile ? '8px 12px' : '6px 12px', fontSize: isMobile ? '0.8rem' : '0.875rem' }} onClick={() => { setSeciliSinav(s.id); setAktifSekme('karneler'); }}>📑 Sıralamayı Aç</button>
                            <button className="btn btn-outline btn-sm" style={{ padding: isMobile ? '8px 12px' : '6px 12px', fontSize: isMobile ? '0.8rem' : '0.875rem', color: '#DC2626', borderColor: 'rgba(220, 38, 38, 0.35)' }} onClick={() => handleSinavSil(s)}>🗑️ Sil</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Henüz sonuç yok</span>
                            <button className="btn btn-outline btn-sm" style={{ padding: isMobile ? '8px 12px' : '6px 12px', fontSize: isMobile ? '0.8rem' : '0.875rem' }} onClick={() => { setSeciliSinav(s.id); setAktifSekme('yukle'); }}>⚙️ Optik Akışı</button>
                            <button className="btn btn-outline btn-sm" style={{ padding: isMobile ? '8px 12px' : '6px 12px', fontSize: isMobile ? '0.8rem' : '0.875rem', color: '#DC2626', borderColor: 'rgba(220, 38, 38, 0.35)' }} onClick={() => handleSinavSil(s)}>🗑️ Sil</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {aktifSekme === 'yukle' && (
        <div style={{ display: 'grid', gap: isMobile ? 12 : 16 }}>
          <div className="card animate-fadeInUp" style={cardStyle}>
            <div style={{ ...flexBetweenStyle, marginBottom: 0 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.3rem' }}>Deneme Optik İşlem Akışı</h3>
                <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: isMobile ? '0.85rem' : '0.95rem' }}>Aynı deneme içinde şablon seçin ve TXT sonuçlarını yükleyin.</p>
              </div>
              <button className="btn btn-primary" style={{ whiteSpace: 'nowrap' }} onClick={() => setShowYeniSinav(true)}>+ Yeni Deneme</button>
            </div>
          </div>

          <div className="card animate-fadeInUp" style={cardStyle}>
            <div style={grid2ColStyle}>
              <div style={formGroupStyle}>
                <label className="form-label">Deneme Seçin</label>
                <select style={selectStyle} value={seciliSinav} onChange={(e) => setSeciliSinav(e.target.value)}>
                  {sinavlar.length === 0 && <option value="">Önce deneme oluşturun</option>}
                  {sinavlar.map((sinav) => <option key={sinav.id} value={sinav.id}>{sinav.ad}</option>)}
                </select>
              </div>
              <div style={{ borderRadius: 14, padding: isMobile ? 14 : 16, background: 'linear-gradient(135deg, rgba(29,78,216,0.06), rgba(225,29,72,0.05))', border: '1px solid rgba(29,78,216,0.12)' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#1D4ED8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Seçili Deneme</div>
                <div style={{ marginTop: 8, fontWeight: 900, fontSize: isMobile ? '1rem' : '1.15rem', color: '#0F172A' }}>{seciliSinavBilgisi?.ad || 'Henüz seçilmedi'}</div>
                <div style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: '0.85rem' }}>{seciliSinavBilgisi ? `${new Date(seciliSinavBilgisi.tarih).toLocaleDateString('tr-TR')} • ${seciliSinavBilgisi.tur || 'Deneme'}` : 'Akışı başlatmak için mevcut denemelerden birini seçin.'}</div>
                {seciliSinavYerel && (
                  <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(249, 115, 22, 0.08)', border: '1px solid rgba(249, 115, 22, 0.2)', color: '#9A3412', fontSize: '0.82rem', fontWeight: 700 }}>
                    Bu deneme sadece tarayıcıda kayıtlı görünüyor. Optik sonuç yüklemek için denemeyi backendde yeniden oluşturmanız gerekiyor.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr', gap: isMobile ? 12 : 16 }}>
            <div style={{ display: 'grid', gap: isMobile ? 12 : 16 }}>
              
              <div className="card animate-fadeInUp" style={optikAdimKartStyle('rgba(29, 78, 216, 0.18)')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(29,78,216,0.1)', color: '#1D4ED8', fontWeight: 900 }}>1</div>
                  <div><div style={{ fontWeight: 900, color: '#0F172A' }}>Optik Şablonu Seç / Yükle</div><div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Kayıtlı şablon seçebilir veya yeni .fmt yükleyebilirsiniz.</div></div>
                </div>
                <div style={formGroupStyle}>
                  <label className="form-label">Kayıtlı Şablonlar</label>
                  <select style={selectStyle} value={seciliSablonId} onChange={(e) => setSeciliSablonId(e.target.value)}>
                    <option value="">Şablon seçin</option>
                    {optikSablonlar.map((sablon) => <option key={sablon.id} value={sablon.id}>{getSablonLabel(sablon)}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: 10, alignItems: 'end', marginBottom: 12 }}>
                  <div style={formGroupStyle}>
                    <label className="form-label">Yeni Şablon Adı</label>
                    <input style={inputStyle} placeholder="Örn: TYT 120 Soru FMT" value={fmtAd} onChange={(e) => setFmtAd(e.target.value)} />
                  </div>
                  <button className="btn btn-outline" onClick={() => fmtInputRef.current?.click()}>.fmt Seç</button>
                </div>
                <input ref={fmtInputRef} type="file" accept=".fmt" style={{ display: 'none' }} onChange={(e) => setFmtDosyasi(e.target.files?.[0] || null)} />
                <div style={dropZoneStyle(Boolean(fmtDosyasi))}>
                  <div style={{ fontSize: '1.9rem', marginBottom: 8 }}>🧩</div>
                  <div style={{ fontWeight: 800, color: '#0F172A' }}>{fmtDosyasi ? fmtDosyasi.name : 'Yeni .fmt optik şablonunu seçin'}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{seciliSablonId ? `Aktif şablon: ${getSablonLabel(optikSablonlar.find((item) => String(item.id) === String(seciliSablonId)))}` : 'Henüz aktif şablon seçilmedi'}</div>
                  <button className="btn btn-primary" disabled={fmtYukleniyor || !fmtDosyasi} onClick={handleFmtUpload}>{fmtYukleniyor ? 'Yükleniyor...' : 'Şablonu Kaydet'}</button>
                </div>
              </div>

              <div className="card animate-fadeInUp" style={optikAdimKartStyle('rgba(225, 29, 72, 0.2)')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(225,29,72,0.12)', color: '#E11D48', fontWeight: 900 }}>1A</div>
                  <div>
                    <div style={{ fontWeight: 900, color: '#0F172A' }}>Cevap Anahtarı Girişi</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Boş alandan manuel girin, isterseniz seçili .fmt dosyasından otomatik doldurun.</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: 10, alignItems: 'end', marginBottom: 12 }}>
                  <div style={formGroupStyle}>
                    <label className="form-label">Sınav Türü</label>
                    <select style={selectStyle} value={sinavTuru} onChange={(e) => handleSinavTuruDegis(e.target.value)}>
                      {Object.keys(EXAM_SUBJECTS).map((tur) => (
                        <option key={tur} value={tur}>{tur}</option>
                      ))}
                    </select>
                  </div>
                  <button className="btn btn-outline" onClick={handleCevapAnahtariFmtDoldur} disabled={!fmtDosyasi}>
                    .fmt ile Doldur
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: isMobile ? 'hidden' : 'auto', flexWrap: isMobile ? 'wrap' : 'nowrap', paddingBottom: 4 }}>
                  {kitapciklarListesi.map(k => (
                    <div key={k} onClick={() => setAktifKitapcik(k)} className={`kitapcik-tab ${aktifKitapcik === k ? 'active' : 'inactive'}`}>
                      {k} Kitapçığı
                    </div>
                  ))}
                  <button onClick={handleYeniKitapcikEkle} style={{ border: '1px dashed #E11D48', background: 'transparent', color: '#E11D48', borderRadius: 8, padding: '0 12px', fontWeight: 800, cursor: 'pointer' }}>+ Yeni</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: 10, alignItems: 'end', marginBottom: 12 }}>
                  <div style={formGroupStyle}>
                    <label className="form-label">Ders Ekle</label>
                    <input
                      style={inputStyle}
                      placeholder="Örn: Geometri"
                      value={txtOgrenYeniDers}
                      onChange={(e) => setTxtOgrenYeniDers(e.target.value)}
                    />
                  </div>
                  <button className="btn btn-outline" onClick={handleTxtOgrenDersEkle}>Ders Ekle</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  {txtOgrenDersListesi.map((ders) => (
                    <div key={ders} style={formGroupStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <label className="form-label" style={{ color: '#0F172A', fontWeight: 800 }}>{ders}</label>
                        <button className="btn btn-outline btn-sm" onClick={() => handleTxtOgrenDersSil(ders)}>Sil</button>
                      </div>
                      <textarea
                        style={{ ...inputStyle, minHeight: 72, resize: 'vertical', fontFamily: 'monospace', letterSpacing: '0.08em', borderColor: '#E2E8F0', background: '#F8FAFC' }}
                        placeholder="ABCDE..."
                        value={cevapAnahtari[aktifKitapcik]?.[ders] || ''}
                        onChange={(e) => handleAnswerKeyChange(ders, e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: 12, border: '1px solid #E2E8F0', borderRadius: 12, padding: 12, background: '#FFFFFF' }}>
                  <div style={{ fontWeight: 900, color: '#0F172A', marginBottom: 10 }}>Ders Bazlı Kazanım Girişi</div>
                  <div style={{ display: 'grid', gap: 10, maxHeight: isMobile ? 300 : 360, overflowY: 'auto', paddingRight: 2 }}>
                    {txtOgrenDersListesi.map((ders) => {
                      const key = normalizeDersAdi(ders);
                      const aralik = getDersOtomatikAralik(key);
                      return (
                        <div key={`kazanim_${key}`} style={{ border: '1px solid #EEF2F7', borderRadius: 10, padding: 10, background: '#F8FAFC' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                            <div style={{ fontWeight: 800, color: '#1E293B' }}>{key}</div>
                            <div style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 700 }}>
                              {aralik.len > 0 ? `${aralik.start}-${aralik.end} (${aralik.len} soru)` : 'Önce cevap anahtarı girin'}
                            </div>
                          </div>
                          <textarea
                            style={{ ...inputStyle, minHeight: 90, resize: 'vertical', whiteSpace: 'pre-wrap' }}
                            placeholder="Kazanımları alt alta yazın. Her satır otomatik sıradaki soruya atanır."
                            value={getKazanimMetni(key)}
                            onChange={(e) => handleKazanimMetniDegis(key, e.target.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                    Seçili .fmt: {fmtDosyasi ? fmtDosyasi.name : 'Yok'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" style={{ background: '#E11D48', border: 'none' }} disabled={cevapAnahtariKaydediliyor || !seciliSinav || seciliSinavYerel} onClick={handleCevapAnahtariKaydet}>
                      {cevapAnahtariKaydediliyor ? 'Kaydediliyor...' : 'Cevap Anahtarını Kaydet'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gap: isMobile ? 12 : 16 }}>
              <div className="card animate-fadeInUp" style={optikAdimKartStyle('rgba(16, 185, 129, 0.18)')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(16,185,129,0.12)', color: '#059669', fontWeight: 900 }}>2</div>
                  <div><div style={{ fontWeight: 900, color: '#0F172A' }}>TXT Sonuç Dosyası Yükle</div><div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Seçili şablon ile eşleşen .txt sonuç dosyasını işleyin.</div></div>
                </div>

                <div style={formGroupStyle}>
                  <label className="form-label">Otomatik / Öğrenilmiş Şablon</label>
                  <select style={selectStyle} value={seciliSablonId} onChange={(e) => setSeciliSablonId(e.target.value)}>
                    <option value="">Otomatik (Öğren)</option>
                    {optikSablonlar.map((sablon) => (
                      <option key={sablon.id} value={sablon.id}>{getSablonLabel(sablon)}</option>
                    ))}
                  </select>
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {seciliSablonId ? `Aktif şablon: ${getSablonLabel(optikSablonlar.find((item) => String(item.id) === String(seciliSablonId)))}` : 'Otomatik öğrenme açık'}
                    </div>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => {
                        resetTxtOgren();
                        setTxtOgrenSablonId(seciliSablonId);
                        setTxtOgrenSablonAd(
                          getSablonLabel(
                            optikSablonlar.find((item) => String(item.id) === String(seciliSablonId))
                          ) || ''
                        );
                        setShowTxtSablonOgren(true);
                      }}
                    >
                      TXT Şablon Öğren
                    </button>
                  </div>
                </div>
                
                <input 
                  ref={txtInputRef} 
                  type="file" 
                  accept=".txt" 
                  multiple 
                  style={{ display: 'none' }} 
                  onChange={(e) => setTxtDosyalari(Array.from(e.target.files || []))} 
                />
                <div style={dropZoneStyle(txtDosyalari.length > 0)} onClick={() => txtInputRef.current?.click()}>
                  <div style={{ fontSize: '1.9rem', marginBottom: 8 }}>📄</div>
                  <div style={{ fontWeight: 800, color: '#0F172A' }}>
                    {txtDosyalari.length > 0 ? `${txtDosyalari.length} adet .txt dosyası seçildi` : 'Sonuç .txt dosyalarını seçin (Çoklu seçebilirsiniz)'}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 6 }}>
                    Desteklenen dosya tipi: `.txt`
                  </div>
                </div>

                <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(148,163,184,0.18)', display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Yükleme kontrolü</div>
                  <div style={{ fontWeight: 800, color: '#0F172A' }}>Deneme: {seciliSinavBilgisi?.ad || '-'}</div>
                  <div style={{ fontWeight: 700, color: '#334155' }}>Şablon: {seciliSablonId ? getSablonLabel(optikSablonlar.find((item) => String(item.id) === String(seciliSablonId))) : 'Seçilmedi'}</div>
                </div>
                
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                  <button className="btn btn-primary" style={{ background: '#10B981', border: 'none' }} disabled={txtYukleniyor || txtFlowLoading || txtDosyalari.length === 0 || !seciliSinav || seciliSinavYerel} onClick={handleTxtUpload}>
                    {txtYukleniyor ? 'İşleniyor...' : 'TXT Dosyalarını Yükle'}
                  </button>
                  <button className="btn btn-outline" onClick={() => loadSonuclarForSinav(seciliSinav, { showToast: true })}>Sonuçları Yenile</button>
                </div>

                {activeUploadMeta && (
                  <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 12, background: 'rgba(15,23,42,0.04)', border: '1px solid rgba(148,163,184,0.18)', display: 'grid', gap: 8 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#475569' }}>Parser / Şablon Bilgisi</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {activeUploadMeta.parser && <span className="badge badge-info">Parser: {activeUploadMeta.parser}</span>}
                      {activeUploadMeta.sablon_adi && <span className="badge badge-success">Şablon: {activeUploadMeta.sablon_adi}</span>}
                      {activeUploadMeta.line_variant && <span className="badge badge-warning">Line: {activeUploadMeta.line_variant}</span>}
                      {typeof activeUploadMeta.confidence_score === 'number' && (
                        <span className="badge badge-info">Güven: {(activeUploadMeta.confidence_score * 100).toFixed(1)}%</span>
                      )}
                    </div>
                    {(() => {
                      const warningInfo = summarizeWarnings(activeUploadMeta.warnings);
                      if (!warningInfo.summary) return null;
                      return (
                        <div style={{ display: 'grid', gap: 6 }}>
                          <div style={{ fontSize: '0.82rem', color: '#B45309', fontWeight: 800 }}>
                            ⚠ {warningInfo.summary}
                          </div>
                          {warningInfo.preview.map((warning, index) => (
                            <div key={`${warning}_${index}`} style={{ fontSize: '0.78rem', color: '#92400E', fontWeight: 700 }}>
                              {index + 1}. {warning}
                            </div>
                          ))}
                          {warningInfo.extraCount > 0 && (
                            <div style={{ fontSize: '0.78rem', color: '#92400E', fontWeight: 700 }}>
                              ... +{warningInfo.extraCount} daha
                            </div>
                          )}
                          <details style={{ marginTop: 2 }}>
                            <summary style={{ cursor: 'pointer', color: '#1D4ED8', fontSize: '0.78rem', fontWeight: 700 }}>Detayları Gör</summary>
                            <pre style={{ marginTop: 6, whiteSpace: 'pre-wrap', fontSize: '0.72rem', color: '#334155' }}>
                              {JSON.stringify(activeUploadMeta.warnings || [], null, 2)}
                            </pre>
                          </details>
                        </div>
                      );
                    })()}
                    {activeUploadMeta.soru_sayilari && Object.keys(activeUploadMeta.soru_sayilari).length > 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                        <strong>Soru Sayıları:</strong>{' '}
                        {Object.entries(activeUploadMeta.soru_sayilari)
                          .map(([ders, adet]) => `${ders}: ${adet}`)
                          .join(' • ')}
                      </div>
                    )}
                    {activeUploadMeta.field_mapping && Object.keys(activeUploadMeta.field_mapping).length > 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                        <strong>Ders Eşleşmeleri:</strong>{' '}
                        {Object.entries(activeUploadMeta.field_mapping)
                          .map(([kaynak, hedef]) => `${kaynak}→${hedef}`)
                          .join(' • ')}
                      </div>
                    )}
                    {typeof activeUploadMeta.confidence_score === 'number' && activeUploadMeta.confidence_score < 0.7 && (
                      <div style={{ fontSize: '0.8rem', color: '#B45309', fontWeight: 700 }} className="badge badge-warning">
                        Düşük güven algılandı, doğrulama önerilir.
                      </div>
                    )}
                  </div>
                )}
                {flowError && (
                  <div style={{ marginTop: 10, borderRadius: 12, border: '1px solid rgba(220,38,38,0.2)', background: 'rgba(254,242,242,0.9)', padding: '10px 12px', color: '#991B1B' }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 800 }}>
                      {(typeof flowError === 'object' ? flowError?.message : String(flowError)) || 'İşlem başarısız oldu.'}
                    </div>
                    {process.env.NODE_ENV === 'development' && typeof flowError === 'object' && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: 'pointer', fontSize: '0.76rem', fontWeight: 700 }}>Detayları Gör</summary>
                        <pre style={{ marginTop: 6, whiteSpace: 'pre-wrap', fontSize: '0.72rem' }}>{JSON.stringify(flowError, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                )}
              </div>

              <div className="card animate-fadeInUp" style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                  <div><h4 style={{ margin: 0, fontSize: isMobile ? '1rem' : '1.1rem' }}>Yükleme Özeti</h4></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                  <div style={{ borderRadius: 14, padding: 14, background: 'rgba(29,78,216,0.06)', border: '1px solid rgba(29,78,216,0.14)' }}><div style={{ fontSize: '0.8rem', color: '#1D4ED8', fontWeight: 800 }}>Toplam Kayıt</div><div style={{ marginTop: 8, fontSize: '1.6rem', fontWeight: 900, color: '#0F172A' }}>{yuklemeOzeti.toplam}</div></div>
                  <div style={{ borderRadius: 14, padding: 14, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.14)' }}><div style={{ fontSize: '0.8rem', color: '#059669', fontWeight: 800 }}>Kayıtlı Öğrenci</div><div style={{ marginTop: 8, fontSize: '1.6rem', fontWeight: 900, color: '#0F172A' }}>{yuklemeOzeti.kayitli}</div></div>
                  <div style={{ borderRadius: 14, padding: 14, background: 'rgba(225,29,72,0.06)', border: '1px solid rgba(225,29,72,0.14)' }}><div style={{ fontSize: '0.8rem', color: '#E11D48', fontWeight: 800 }}>Misafir</div><div style={{ marginTop: 8, fontSize: '1.6rem', fontWeight: 900, color: '#0F172A' }}>{yuklemeOzeti.misafir}</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {aktifSekme === 'karneler' && (
        <div className="card animate-fadeInUp" style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-end', marginBottom: isMobile ? 16 : 20, flexWrap: 'wrap', gap: isMobile ? 12 : 10, flexDirection: isMobile ? 'column' : 'row' }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}>
              <label className="form-label" style={{ fontSize: isMobile ? '0.85rem' : 'inherit' }}>Sınav Seçin</label>
              <select style={{ ...selectStyle, fontWeight: 700, fontSize: isMobile ? '0.95rem' : '1.05rem', color: '#1E293B' }} value={seciliSinav} onChange={e => setSeciliSinav(e.target.value)}>
                {sinavlar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}>
              <label className="form-label" style={{ fontSize: isMobile ? '0.85rem' : 'inherit' }}>Öğrenci Ara</label>
              <input style={inputStyle} placeholder="İsim veya numara ile ara..." value={arama} onChange={e => setArama(e.target.value)} />
            </div>
            <div className="no-print" style={{ minWidth: isMobile ? '100%' : 220 }}>
              <label className="form-label" style={{ opacity: 0 }}>Toplu</label>
              <button
                className="btn btn-primary"
                style={{ width: '100%', background: '#1E293B', color: 'white', border: 'none', padding: isMobile ? '12px' : '10px 16px', whiteSpace: 'nowrap' }}
                onClick={handleTopluPdfIndir}
                disabled={pdfOlusturuluyor}
              >
                {pdfOlusturuluyor ? 'PDF Hazırlanıyor...' : '📄 Toplu PDF İndir (A4)'}
              </button>
              {pdfProgress && (
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--text-muted)' }}>{pdfProgress}</div>
              )}
            </div>
          </div>
          {sonuclar.length === 0 ? (
            <div className="empty-state" style={{ padding: isMobile ? 30 : 40 }}><p style={{ fontSize: isMobile ? '0.9rem' : 'inherit' }}>Bu sınav için henüz optik sonuç yüklenmemiş.</p></div>
          ) : (
            <div style={tableWrapperStyle}>
              <table className="table responsive-table" style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ width: 60, textAlign: 'center' }}>Sıra</th>
                    <th>Öğrenci</th>
                    <th className="hide-mobile">Durum</th>
                    <th className="hide-mobile-table">Kitapçık</th>
                    <th>Toplam Net</th>
                    <th style={{ textAlign: 'right' }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrelenmisSonuclar.map((s, index) => (
                    <tr key={getSonucRowKey(s, index)} style={{ background: s.tip === 'misafir' ? '#FFFBEB' : 'transparent' }}>
                      <td style={{ textAlign: 'center', fontWeight: 800, color: 'var(--text-muted)' }}>#{s.siralama}</td>
                      <td>
                        <div style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: isMobile ? '0.85rem' : 'inherit' }}>
                          {s.adSoyad || `${s.ad || ''} ${s.soyad || ''}`.trim() || 'İsimsiz Öğrenci'}
                        </div>
                        <div style={{ fontSize: isMobile ? '0.7rem' : '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          No: {s.numara || '-'} • {s.sinif || '-'}
                        </div>
                      </td>
                      <td className="hide-mobile"><span className={`badge ${s.tip === 'kayitli' ? 'badge-success' : 'badge-warning'}`}>{s.tip === 'kayitli' ? '🏫 Kurum Öğrencisi' : '🌍 Misafir'}</span></td>
                      <td className="hide-mobile-table"><span style={{ background: '#E2E8F0', padding: '4px 8px', borderRadius: 6, fontWeight: 900, color: '#334155' }}>{s.kitapcik || 'A'}</span></td>
                      <td style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, color: '#3B82F6', fontSize: isMobile ? '1rem' : '1.1rem' }}>{s.toplamNet}</td>
                      <td style={{ textAlign: isMobile ? 'left' : 'right' }}>
                        <button className="btn btn-primary btn-sm" style={{ background: 'linear-gradient(45deg, #3B82F6, #8B5CF6)', border: 'none', padding: isMobile ? '8px 10px' : '6px 12px', fontSize: isMobile ? '0.75rem' : '0.875rem', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }} onClick={() => setSeciliKarne(normalizeKarneRecord(s))}>✨ Karne Göster</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Sınav Ekleme Modal'ı */}
      {showYeniSinav && (
        <div style={modalBackdropStyle} onClick={() => setShowYeniSinav(false)}>
          <div style={{ ...modalStyle, maxWidth: isMobile ? '100%' : 500, padding: 20 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid #E2E8F0', paddingBottom: 16 }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>📝 Yeni Sınav Tanımla</div>
              <button onClick={() => setShowYeniSinav(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={formGroupStyle}>
                <label className="form-label">Sınav Adı *</label>
                <input style={inputStyle} placeholder="Örn: Türkiye Geneli TYT Denemesi" value={yeniSinavForm.ad} onChange={e => setYeniSinavForm({...yeniSinavForm, ad: e.target.value})} />
              </div>
              <div style={grid2ColStyle}>
                <div style={formGroupStyle}>
                  <label className="form-label">Tarih *</label>
                  <input style={inputStyle} type="date" value={yeniSinavForm.tarih} onChange={e => setYeniSinavForm({...yeniSinavForm, tarih: e.target.value})} />
                </div>
                <div style={formGroupStyle}>
                  <label className="form-label">Sınav Türü</label>
                  <select style={selectStyle} value={yeniSinavForm.tur} onChange={e => setYeniSinavForm({...yeniSinavForm, tur: e.target.value})}>
                    <option value="TYT">TYT</option>
                    <option value="AYT">AYT</option>
                    <option value="LGS">LGS</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                <button className="btn btn-primary" style={{ flex: 1, padding: '10px 20px' }} onClick={handleYeniSinavKaydet}>✓ Kaydet</button>
                <button className="btn btn-outline" style={{ padding: '10px 20px' }} onClick={() => setShowYeniSinav(false)}>İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTxtSablonOgren && (
        <div style={modalBackdropStyle} onClick={() => { setShowTxtSablonOgren(false); resetTxtOgren(); }}>
          <div
            style={{
              ...modalStyle,
              width: isMobile ? '100%' : 'min(1320px, calc(100vw - 40px))',
              maxWidth: '100vw',
              maxHeight: isMobile ? '92vh' : 'calc(100vh - 32px)',
              padding: 0,
              borderRadius: isMobile ? '22px 22px 0 0' : 24,
              overflowY: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: isMobile ? '22px 18px 16px' : '26px 30px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
              <div>
                <div style={{ fontSize: isMobile ? '1.4rem' : '1.8rem', fontWeight: 900, color: '#0F172A' }}>
                  TXT Şablon Öğrenme
                </div>
                <div style={{ marginTop: 8, color: '#64748B', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                  Büyük alanda örnek satırı gör, alan seç, kaydet. Adım adım ileri-geri yok.
                </div>
              </div>
              <button
                onClick={() => { setShowTxtSablonOgren(false); resetTxtOgren(); }}
                style={{ background: 'none', border: 'none', fontSize: '2rem', lineHeight: 1, cursor: 'pointer', color: '#0F172A' }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: isMobile ? 18 : 24, display: 'grid', gap: 18 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr', gap: 16, alignItems: 'end' }}>
                <div style={formGroupStyle}>
                  <label className="form-label">TXT Dosyaları</label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <input
                      ref={txtOgrenFileRef}
                      type="file"
                      accept=".txt"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => setTxtOgrenFiles(Array.from(e.target.files || []))}
                    />
                    <button className="btn btn-outline" onClick={() => txtOgrenFileRef.current?.click()}>
                      TXT Seç
                    </button>
                    <button className="btn btn-primary" disabled={txtOgrenLoading || txtOgrenFiles.length === 0} onClick={handleTxtOgrenPreview}>
                      {txtOgrenLoading ? 'Hazırlanıyor...' : 'Örnek Satırları Getir'}
                    </button>
                  </div>
                  <div style={{ marginTop: 8, color: '#64748B', fontSize: '0.9rem' }}>
                    {txtOgrenFiles.length > 0
                      ? `${txtOgrenFiles.length} adet TXT seçildi`
                      : 'Bir veya birden fazla TXT seçip örnek satırları getir.'}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  <div style={formGroupStyle}>
                    <label className="form-label">Şablon Adı</label>
                    <input
                      style={inputStyle}
                      value={txtOgrenSablonAd}
                      onChange={(e) => setTxtOgrenSablonAd(e.target.value)}
                      placeholder="Örn: 3D TYT TXT Şablonu"
                    />
                  </div>
                  <div style={formGroupStyle}>
                    <label className="form-label">Satır Modu</label>
                    <select style={selectStyle} value={txtOgrenLineMode} onChange={(e) => setTxtOgrenLineMode(e.target.value)}>
                      <option value="single">Tek Satır</option>
                      <option value="merge_pairs">Çift Satırı Birleştir</option>
                      <option value="pad_merge_pairs">Pad + Birleştir</option>
                      <option value="odd_lines">Tek Numara Satırlar</option>
                      <option value="even_lines">Çift Numara Satırlar</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr', gap: 12 }}>
                <div style={{ borderRadius: 16, border: '1px solid #E2E8F0', background: '#FFFFFF', padding: 16 }}>
                  <div style={{ fontWeight: 900, color: '#0F172A', marginBottom: 12 }}>Cevap Anahtarı (A / B)</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: isMobile ? 'hidden' : 'auto', flexWrap: isMobile ? 'wrap' : 'nowrap', paddingBottom: 4 }}>
                    {kitapciklarListesi.map(k => (
                      <div key={k} onClick={() => setAktifKitapcik(k)} className={`kitapcik-tab ${aktifKitapcik === k ? 'active' : 'inactive'}`}>
                        {k} Kitapçığı
                      </div>
                    ))}
                    <button onClick={handleYeniKitapcikEkle} style={{ border: '1px dashed #E11D48', background: 'transparent', color: '#E11D48', borderRadius: 8, padding: '0 12px', fontWeight: 800, cursor: 'pointer' }}>+ Yeni</button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr auto', gap: 10, alignItems: 'end', marginBottom: 12 }}>
                    <div style={formGroupStyle}>
                      <label className="form-label">Ders Ekle</label>
                      <input
                        style={inputStyle}
                        placeholder="Örn: Türkçe"
                        value={txtOgrenYeniDers}
                        onChange={(e) => setTxtOgrenYeniDers(e.target.value)}
                      />
                    </div>
                    <button className="btn btn-outline" onClick={handleTxtOgrenDersEkle}>Ders Ekle</button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                    {txtOgrenDersListesi.map((ders) => {
                      const raw = String(cevapAnahtari[aktifKitapcik]?.[ders] || '');
                      const length = raw.replace(/\s+/g, '').length;
                      return (
                        <div key={ders} style={formGroupStyle}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <label className="form-label" style={{ color: '#0F172A', fontWeight: 800 }}>{ders}</label>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span style={{ fontSize: '0.78rem', color: '#64748B' }}>{length} soru</span>
                              <button className="btn btn-outline btn-sm" onClick={() => handleTxtOgrenDersSil(ders)}>Sil</button>
                            </div>
                          </div>
                          <textarea
                            style={{ ...inputStyle, minHeight: 86, resize: 'vertical', fontFamily: 'monospace', letterSpacing: '0.08em', borderColor: '#E2E8F0', background: '#F8FAFC' }}
                            placeholder="ABCDE..."
                            value={cevapAnahtari[aktifKitapcik]?.[ders] || ''}
                            onChange={(e) => handleAnswerKeyChange(ders, e.target.value)}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: 12, border: '1px solid #E2E8F0', borderRadius: 12, padding: 12, background: '#FFFFFF' }}>
                    <div style={{ fontWeight: 900, color: '#0F172A', marginBottom: 10 }}>Ders Bazlı Kazanım Girişi</div>
                    <div style={{ display: 'grid', gap: 10, maxHeight: isMobile ? 260 : 320, overflowY: 'auto', paddingRight: 2 }}>
                      {txtOgrenDersListesi.map((ders) => {
                        const key = normalizeDersAdi(ders);
                        const aralik = getDersOtomatikAralik(key);
                        return (
                          <div key={`txt_kazanim_${key}`} style={{ border: '1px solid #EEF2F7', borderRadius: 10, padding: 10, background: '#F8FAFC' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                              <div style={{ fontWeight: 800, color: '#1E293B' }}>{key}</div>
                              <div style={{ fontSize: '0.78rem', color: '#64748B', fontWeight: 700 }}>
                                {aralik.len > 0 ? `${aralik.start}-${aralik.end} (${aralik.len} soru)` : 'Önce cevap anahtarı girin'}
                              </div>
                            </div>
                            <textarea
                              style={{ ...inputStyle, minHeight: 90, resize: 'vertical', whiteSpace: 'pre-wrap' }}
                              placeholder="Kazanımları alt alta yazın. Her satır otomatik sıradaki soruya atanır."
                              value={getKazanimMetni(key)}
                              onChange={(e) => handleKazanimMetniDegis(key, e.target.value)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '0.85rem', color: '#64748B' }}>
                      Beklenen toplam uzunluk: <strong>{txtOgrenExpectedLength || '-'}</strong>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#94A3B8' }}>
                      Ders alanlarını cevap anahtarı sırasına göre ayrı ayrı kaydedin veya otomatik bölmeyi kullanın.
                    </div>
                    <button className="btn btn-primary" style={{ background: '#E11D48', border: 'none' }} disabled={cevapAnahtariKaydediliyor || !seciliSinav || seciliSinavYerel} onClick={handleCevapAnahtariKaydet}>
                      {cevapAnahtariKaydediliyor ? 'Kaydediliyor...' : 'Tüm Kitapçıkları Kaydet'}
                    </button>
                  </div>
                </div>
              </div>

              {txtOgrenError && (
                <div style={{ borderRadius: 14, border: '1px solid rgba(220,38,38,0.18)', background: 'rgba(254,242,242,0.95)', padding: '14px 16px', color: '#991B1B' }}>
                  <div style={{ fontWeight: 800 }}>{txtOgrenError.message}</div>
                  {process.env.NODE_ENV === 'development' && txtOgrenError.debug && (
                    <pre style={{ marginTop: 10, fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#7F1D1D' }}>
                      {JSON.stringify(txtOgrenError.debug, null, 2)}
                    </pre>
                  )}
                </div>
              )}

              {txtOgrenSamples.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.65fr 0.95fr', gap: 18, alignItems: 'start' }}>
                  <div style={{ display: 'grid', gap: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontWeight: 900, color: '#0F172A', fontSize: '1.05rem' }}>Örnek Satır Alanı</div>
                        <div style={{ color: '#64748B', fontSize: '0.9rem', marginTop: 4 }}>
                          Aktif alan: <span style={{ fontWeight: 800, color: '#1D4ED8' }}>{getTxtTemplateFieldLabel(txtOgrenActiveField)}</span>
                          {' '}• {getTxtTemplateFieldHint(txtOgrenActiveField)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {txtOgrenSamples.map((_, index) => (
                          <button
                            key={index}
                            className="btn btn-outline btn-sm"
                            onClick={() => handleTxtOgrenSamplePick(index)}
                            style={{
                              borderColor: txtOgrenSelectedSample === index ? '#2563EB' : undefined,
                              background: txtOgrenSelectedSample === index ? 'rgba(37,99,235,0.08)' : undefined,
                              color: txtOgrenSelectedSample === index ? '#1D4ED8' : undefined,
                              fontWeight: 800,
                            }}
                          >
                            Satır {index + 1}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ border: '1px solid #CBD5E1', borderRadius: 18, overflow: 'hidden', background: '#F8FAFC' }}>
                      <textarea
                        ref={txtOgrenLineRef}
                        readOnly
                        value={txtOgrenSample}
                        onSelect={handleTxtOgrenRangeFromSelect}
                        onMouseUp={handleTxtOgrenRangeFromSelect}
                        onKeyUp={handleTxtOgrenRangeFromSelect}
                        style={{
                          width: '100%',
                          minHeight: isMobile ? 240 : 360,
                          border: 'none',
                          outline: 'none',
                          resize: 'none',
                          background: 'transparent',
                          padding: isMobile ? 16 : 20,
                          fontFamily: 'Courier New, monospace',
                          fontSize: isMobile ? '1rem' : '1.05rem',
                          lineHeight: 1.55,
                          whiteSpace: 'pre',
                          overflowX: 'auto',
                          color: '#0F172A',
                        }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
                      <div style={formGroupStyle}>
                        <label className="form-label">Başlangıç</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={txtOgrenRange.start}
                          onChange={(e) => setTxtOgrenRange(prev => ({ ...prev, start: e.target.value }))}
                          placeholder="1"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label className="form-label">Bitiş</label>
                        <input
                          type="number"
                          style={inputStyle}
                          value={txtOgrenRange.end}
                          onChange={(e) => setTxtOgrenRange(prev => ({ ...prev, end: e.target.value }))}
                          placeholder="20"
                        />
                      </div>
                      <div style={formGroupStyle}>
                        <label className="form-label">Seçilen Metin</label>
                        <input style={inputStyle} readOnly value={txtOgrenPreviewSelection} placeholder="Satır üzerinden seçim yapın" />
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="btn btn-outline" onClick={handleTxtOgrenAutoSplitAnswers}>
                          Cevap Bloğunu Otomatik Böl
                        </button>
                        <button className="btn btn-primary" style={{ minWidth: 180 }} onClick={handleTxtOgrenAddSelection}>
                          {txtOgrenSelectionMap[txtOgrenActiveField] ? 'Güncelle' : 'Kaydet'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: 14 }}>
                    <div style={{ borderRadius: 18, border: '1px solid #E2E8F0', background: '#FFFFFF', padding: 16 }}>
                      <div style={{ fontWeight: 900, color: '#0F172A', marginBottom: 12, fontSize: '1rem' }}>Alan Listesi</div>
                      <div style={{ display: 'grid', gap: 10 }}>
                        {txtOgrenFieldList.map((field) => {
                          const mevcut = txtOgrenSelectionMap[field];
                          const active = txtOgrenActiveField === field;
                          return (
                            <button
                              key={field}
                              onClick={() => handleTxtOgrenFieldPick(field)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 10,
                                width: '100%',
                                textAlign: 'left',
                                borderRadius: 14,
                                border: `1px solid ${active ? '#2563EB' : mevcut ? 'rgba(16,185,129,0.35)' : '#E2E8F0'}`,
                                background: active ? 'rgba(37,99,235,0.08)' : mevcut ? 'rgba(16,185,129,0.06)' : '#fff',
                                padding: '12px 14px',
                                cursor: 'pointer',
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 800, color: '#0F172A' }}>{getTxtTemplateFieldLabel(field)}</div>
                                <div style={{ fontSize: '0.82rem', color: '#64748B', marginTop: 4 }}>{getTxtTemplateFieldHint(field)}</div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                {mevcut ? (
                                  <>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#059669' }}>{mevcut.start}-{mevcut.end}</div>
                                    <div style={{ fontSize: '0.74rem', color: '#64748B' }}>kaydedildi</div>
                                  </>
                                ) : (
                                  <div style={{ fontSize: '0.78rem', fontWeight: 800, color: active ? '#1D4ED8' : '#94A3B8' }}>
                                    {active ? 'aktif' : 'seçilecek'}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ borderRadius: 18, border: '1px solid #E2E8F0', background: '#FFFFFF', padding: 16 }}>
                      <div style={{ fontWeight: 900, color: '#0F172A', marginBottom: 12, fontSize: '1rem' }}>Kaydedilen Seçimler</div>
                      {txtOgrenSelections.length === 0 ? (
                        <div style={{ color: '#94A3B8', fontSize: '0.9rem' }}>Henüz seçim yapılmadı.</div>
                      ) : (
                        <div style={{ display: 'grid', gap: 8 }}>
                          {txtOgrenFieldList.filter((field) => txtOgrenSelectionMap[field]).map((field) => {
                            const item = txtOgrenSelectionMap[field];
                            return (
                              <div key={field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid #E2E8F0', borderRadius: 12, padding: '10px 12px', background: '#F8FAFC' }}>
                                <div>
                                  <div style={{ fontWeight: 800, color: '#0F172A' }}>{getTxtTemplateFieldLabel(field)}</div>
                                  <div style={{ fontSize: '0.8rem', color: '#64748B' }}>{item.start} - {item.end}</div>
                                </div>
                                <button className="btn btn-outline btn-sm" onClick={() => handleTxtOgrenRemoveSelection(field)}>
                                  Sil
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid #E2E8F0', padding: isMobile ? '16px 18px 20px' : '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: '#fff' }}>
              <div style={{ color: txtOgrenCanSave ? '#059669' : '#B45309', fontWeight: 700, fontSize: '0.9rem' }}>
                {txtOgrenCanSave
                  ? 'Şablon kaydetmeye hazır.'
                  : `Eksik alanlar: ${txtOgrenMissingFields.map((field) => getTxtTemplateFieldLabel(field)).join(', ') || 'Yok'}`}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-outline" onClick={() => { setShowTxtSablonOgren(false); resetTxtOgren(); }}>
                  Vazgeç
                </button>
                <button
                  className="btn btn-primary"
                  style={{ background: '#10B981', border: 'none' }}
                  disabled={!txtOgrenCanSave || txtOgrenFiles.length === 0 || txtYukleniyor || txtFlowLoading || txtOgrenLoading || !seciliSinav || seciliSinavYerel}
                  onClick={handleTxtOgrenSaveAndUpload}
                >
                  {(savingTemplate || uploadingTxt) ? 'İşleniyor...' : 'Kaydet ve Sonuçları Gör'}
                </button>
                <button className="btn btn-primary" disabled={!txtOgrenCanSave || txtOgrenLoading || txtFlowLoading} onClick={handleTxtOgrenSave}>
                  {txtOgrenLoading ? 'Kaydediliyor...' : 'Şablonu Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ekrandaki Eğlenceli Karne Modalı (UI olarak kalmaya devam ediyor) */}
      {seciliKarne && (
        <div className="modal-backdrop no-print" style={modalBackdropStyle} onClick={() => setSeciliKarne(null)}>
          <div id="karne-print-area" style={{ ...modalStyle, padding: 0 }} onClick={e => e.stopPropagation()}>
            {(() => {
              const detayMap = (seciliKarne?.detaylar || []).reduce((acc, item) => {
                const normalized = normalizeDetayItem(item, item?.ders);
                const key = normalizeLessonToken(normalized?.ders);
                if (!key) return acc;

                const mevcut = acc[key];
                if (!mevcut) {
                  acc[key] = normalized;
                  return acc;
                }

                const mevcutDoluluk = Number(mevcut?.dogru || 0) + Number(mevcut?.yanlis || 0) + Number(mevcut?.bos || 0);
                const yeniDoluluk = Number(normalized?.dogru || 0) + Number(normalized?.yanlis || 0) + Number(normalized?.bos || 0);
                const mevcutSoru = Number(mevcut?.soru || 0);
                const yeniSoru = Number(normalized?.soru || 0);

                // Aynı ders için mükerrer kayıt gelirse "daha dolu" olanı kullan.
                if (yeniDoluluk > mevcutDoluluk || (yeniDoluluk === mevcutDoluluk && yeniSoru > mevcutSoru)) {
                  acc[key] = normalized;
                }
                return acc;
              }, {});
              const fixedDetaylar = KARNE_CORE_DERSLER.map((ders) => {
                const key = normalizeLessonToken(ders);
                return detayMap[key] || normalizeDetayItem({ ders, soru: 0, dogru: 0, yanlis: 0, bos: 0, net: 0 }, ders);
              });
              const derivedKonuAnaliz = buildKonuAnalizFromSablon(seciliKarne);
              const displayLessonStats = buildDisplayLessonStats(seciliKarne, fixedDetaylar);
              const apiKonuAnaliz = seciliKarne?.konuAnaliz && typeof seciliKarne.konuAnaliz === 'object' ? seciliKarne.konuAnaliz : {};
              const resolvedKonuAnaliz = {};
              KARNE_CORE_DERSLER.forEach((ders) => {
                const fromApi = apiKonuAnaliz[ders];
                if (Array.isArray(fromApi) && fromApi.length > 0) {
                  resolvedKonuAnaliz[ders] = fromApi;
                  return;
                }
                const fromDerived = derivedKonuAnaliz?.[ders];
                if (Array.isArray(fromDerived) && fromDerived.length > 0) {
                  resolvedKonuAnaliz[ders] = fromDerived;
                }
              });
              const konuGruplari = Object.entries(resolvedKonuAnaliz);
              const konuOzet = konuGruplari.flatMap(([, konular]) => konular);

              // Genel dağılım ve ders sayaçları, toplam nete en tutarlı kaynaktan hesaplanmalı.
              const displayDetaylar = KARNE_CORE_DERSLER.map((ders) => {
                const key = normalizeLessonToken(ders);
                return displayLessonStats[key] || normalizeDetayItem({ ders, soru: 0, dogru: 0, yanlis: 0, bos: 0, net: 0 }, ders);
              });
              const toplamSoru = displayDetaylar.reduce((sum, item) => sum + (item.soru || 0), 0);
              const toplamDogru = displayDetaylar.reduce((sum, item) => sum + (item.dogru || 0), 0);
              const toplamYanlis = displayDetaylar.reduce((sum, item) => sum + (item.yanlis || 0), 0);
              const toplamBos = Math.max(0, toplamSoru - toplamDogru - toplamYanlis);
              const pieData = [
                { name: 'Dogru', value: toplamDogru, color: '#10B981' },
                { name: 'Yanlis', value: toplamYanlis, color: '#EF4444' },
                { name: 'Bos', value: toplamBos, color: '#CBD5E1' },
              ].filter(item => item.value > 0);
              
              const topicSummary = KARNE_CORE_DERSLER.map((ders) => {
                const stats = displayLessonStats[normalizeLessonToken(ders)]
                  || normalizeDetayItem({ ders, soru: 0, dogru: 0, yanlis: 0, bos: 0, net: 0 }, ders);
                return {
                  ders,
                  konular: resolvedKonuAnaliz[ders] || [],
                  dogru: Number(stats?.dogru || 0),
                  yanlis: Number(stats?.yanlis || 0),
                };
              });
              const findTopicEntryForLesson = (lessonName) =>
                topicSummary.find((item) => normalizeLessonToken(item.ders) === normalizeLessonToken(lessonName));
              
              const zayifKonular = [...konuOzet]
                .filter(konu => (konu.basari ?? 0) < 50)
                .sort((a, b) => (a.basari ?? 0) - (b.basari ?? 0))
                .slice(0, 3);
                
              const safeAd = String(seciliKarne?.ad || '').trim();
              const safeSoyad = String(seciliKarne?.soyad || '').trim();
              const safeFullName = `${safeAd} ${safeSoyad}`.trim() || String(seciliKarne?.adSoyad || 'İsimsiz Öğrenci');
              const avatarInitials = `${safeAd.charAt(0) || 'Ö'}${safeSoyad.charAt(0) || ''}`;
              const gelisimMetni = zayifKonular.length
                ? `${safeFullName}, ${zayifKonular.map(konu => `"${konu.konu}"`).join(', ')} konularında gelişim alanı görünüyor. Bu başlıklara odaklanıp önce konu özeti, ardından kısa soru setleriyle ilerlemek net artışını hızlandırır.`
                : `${safeFullName}, bu karneye göre dengeli bir performans gösteriyor. Düzenli tekrar ve deneme analiziyle mevcut çizgiyi koruyabilirsin.`;

              const topicGridStyle = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px minmax(0, 1fr)', gap: isMobile ? 8 : 12, alignItems: 'start' };

              return (
                <>
                  <div style={karneHeaderStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-start', gap: isMobile ? 12 : 24, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                      <div>
                        <h2 style={{ fontFamily: 'var(--font-ui)', margin: 0, fontSize: isMobile ? '1.1rem' : 'clamp(1.25rem, 2vw, 1.75rem)', letterSpacing: '-0.03em', opacity: 0.96 }}>Dershane Pro</h2>
                        <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.78)', fontSize: isMobile ? '0.65rem' : '0.76rem', letterSpacing: '0.16em', textTransform: 'uppercase' }}>Öğrenci Performans Analizi</p>
                      </div>
                      <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                        <div style={{ marginTop: 2, color: 'rgba(255,255,255,0.72)', fontSize: '0.8rem', fontWeight: 700 }}>Sınav Tarihi</div>
                        <div style={{ marginTop: 4, color: '#E2E8F0', fontSize: isMobile ? '0.9rem' : '1rem', fontWeight: 800 }}>{seciliSinavBilgisi ? new Date(seciliSinavBilgisi.tarih).toLocaleDateString('tr-TR') : new Date().toLocaleDateString('tr-TR')}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: isMobile ? '16px' : '20px 24px 12px', background: '#F7F8FB' }}>
                    <div style={karneProfileGrid}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 18, minWidth: 0 }}>
                        <div style={avatarStyle}>{avatarInitials}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: isMobile ? '1.2rem' : 'clamp(1.6rem, 3vw, 2.4rem)', fontWeight: 900, color: '#1F2A44', letterSpacing: '-0.04em', lineHeight: 1.02, wordBreak: 'break-word' }}>
                            {safeFullName}
                          </div>
                          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                            <div style={{ background: '#E9EDF3', color: '#64748B', borderRadius: 12, padding: isMobile ? '8px 12px' : '10px 14px', minWidth: isMobile ? 'auto' : 135 }}>
                              <div style={{ fontSize: '0.74rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Öğrenci No</div>
                              <div style={{ fontSize: '0.98rem', fontWeight: 800, marginTop: 4 }}>{seciliKarne.numara}</div>
                            </div>
                            <div style={{ background: seciliKarne.tip === 'kayitli' ? '#ECFDF5' : '#FFF1F2', color: seciliKarne.tip === 'kayitli' ? '#059669' : '#F43F5E', borderRadius: 12, padding: isMobile ? '8px 12px' : '10px 14px', border: `1px solid ${seciliKarne.tip === 'kayitli' ? '#BBF7D0' : '#FFD6DA'}`, minWidth: isMobile ? 'auto' : 190 }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 800 }}>
                                {seciliKarne.tip === 'kayitli' ? seciliKarne.sinif : 'Dışarıdan (Misafir)'}
                                {seciliKarne.kitapcik && ` • ${seciliKarne.kitapcik} Kitapçığı`}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style={grid3ColStyle}>
                        <div style={statCardStyle}>
                          <div style={{ color: '#60A5FA', fontWeight: 800, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Toplam Net</div>
                          <div style={{ color: '#2563EB', fontWeight: 900, fontSize: isMobile ? '1.4rem' : 'clamp(1.6rem, 3vw, 2.35rem)', lineHeight: 1, marginTop: 8 }}>{seciliKarne.toplamNet}</div>
                        </div>
                        <div style={statCardStyle}>
                          <div style={{ color: '#34D399', fontWeight: 800, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Puan</div>
                          <div style={{ color: '#10B981', fontWeight: 900, fontSize: isMobile ? '1.2rem' : 'clamp(1.35rem, 2.5vw, 2rem)', lineHeight: 1.05, marginTop: 8 }}>{seciliKarne.puan}</div>
                        </div>
                        <div style={rankCardStyle}>
                          <div style={{ fontWeight: 800, fontSize: '0.76rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sıralama</div>
                          <div style={{ fontWeight: 900, fontSize: isMobile ? '1.3rem' : 'clamp(1.5rem, 2.8vw, 2.25rem)', lineHeight: 1, marginTop: 10 }}>#{seciliKarne.siralama}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: isMobile ? '0 16px 12px' : '0 20px 14px', background: '#F7F8FB' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 14, borderTop: '1px solid #E2E8F0' }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#F43F5E', boxShadow: '0 0 0 3px rgba(244,63,94,0.12)' }} />
                      <h3 style={{ margin: 0, fontSize: isMobile ? '0.85rem' : '0.96rem', fontWeight: 900, color: '#1F2A44', letterSpacing: '-0.01em' }}>
                        Konu Bazlı Performans Analizi
                      </h3>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: 10, padding: isMobile ? '0 16px 16px' : '0 20px 16px', background: '#F7F8FB' }}>
                    <div style={topicGridStyle}>
                      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 16, padding: isMobile ? 10 : 12, position: isMobile ? 'static' : 'sticky', top: 0 }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#334155', fontFamily: 'var(--font-ui)', fontSize: '0.82rem' }}>Genel Dağılım</h4>
                        <div style={{ height: isMobile ? 120 : 146 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={isMobile ? 28 : 36} outerRadius={isMobile ? 44 : 54} paddingAngle={3}>
                                {pieData.map((entry, index) => <Cell key={`pie-${index}`} fill={entry.color} />)}
                              </Pie>
                              <RechartsTooltip formatter={(value, name) => [`${value}`, name]} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div style={{ display: 'grid', gap: 5, fontSize: '0.76rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}><span>Toplam Soru</span><span>{toplamSoru}</span></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#10B981', fontWeight: 700 }}><span>Doğru</span><span>{toplamDogru}</span></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#EF4444', fontWeight: 700 }}><span>Yanlış</span><span>{toplamYanlis}</span></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B', fontWeight: 700 }}><span>Boş</span><span>{toplamBos}</span></div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gap: 10 }}>
                        {topicSummary.map(({ ders, konular, dogru, yanlis }) => (
                        <div key={ders} style={{ background: '#FFFFFF', border: '1px solid #E5EAF2', borderRadius: 18, overflow: 'hidden', boxShadow: '0 3px 10px rgba(15,23,42,0.025)' }}>
                          <button
                            onClick={() => setExpandedLesson(expandedLesson === ders ? null : ders)}
                            style={{ width: '100%', background: 'transparent', border: 'none', padding: isMobile ? '8px 10px' : '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 12 }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14, minWidth: 0, flex: 1 }}>
                              <div style={{ width: isMobile ? 30 : 34, height: isMobile ? 30 : 34, borderRadius: 11, background: '#F8FAFC', border: '1px solid #E5EAF2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4F83FF', fontSize: isMobile ? '0.7rem' : '0.82rem', flexShrink: 0 }}>
                                {getLessonIcon(ders)}
                              </div>
                              <div style={{ textAlign: 'left', minWidth: 0 }}>
                                <div style={{ fontSize: isMobile ? '0.7rem' : '0.78rem', fontWeight: 800, color: '#334155', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {getCleanLessonLabel(ders)}
                                </div>
                                <div style={{ display: 'flex', gap: 10, marginTop: 3, fontSize: isMobile ? '0.6rem' : '0.68rem', fontWeight: 700, flexWrap: 'wrap' }}>
                                  <span style={{ color: '#10B981' }}>Doğru: {dogru}</span>
                                  <span style={{ color: '#F43F5E' }}>Yanlış: {yanlis}</span>
                                  <span style={{ color: '#64748B' }}>Konu: {konular.length}</span>
                                </div>
                              </div>
                            </div>
                            <div style={{ color: '#94A3B8', fontSize: '0.98rem', lineHeight: 1, flexShrink: 0 }}>{expandedLesson === ders ? '⌃' : '⌄'}</div>
                          </button>

                          {expandedLesson === ders && (
                            <div style={{ padding: isMobile ? '0 10px 10px' : '0 12px 12px' }}>
                              <div style={{ background: '#F8FAFC', border: '1px solid #E8EDF3', borderRadius: 14, padding: isMobile ? '4px 6px 2px' : '6px 8px 2px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1.5fr 0.8fr 1fr' : '1.8fr 0.7fr 0.9fr', gap: 8, padding: isMobile ? '4px 2px 6px' : '6px 4px 8px', color: '#94A3B8', fontWeight: 800, fontSize: isMobile ? '0.55rem' : '0.62rem', letterSpacing: '0.06em' }}>
                                  <div>KONU</div>
                                  <div style={{ textAlign: 'center' }}>S/D/Y</div>
                                  <div style={{ textAlign: 'right' }}>BAŞARI</div>
                                </div>
                                <div>
                                  {konular.map((konu, index) => {
                                    const renk = (konu.basari ?? 0) >= 75 ? '#10B981' : (konu.basari ?? 0) >= 40 ? '#F59E0B' : '#F43F5E';
                                    const arka = (konu.basari ?? 0) >= 75 ? '#D1FAE5' : (konu.basari ?? 0) >= 40 ? '#FEF3C7' : '#FFE1E6';
                                    return (
                                      <div key={`${ders}-${index}`} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1.5fr 0.8fr 1fr' : '1.8fr 0.7fr 0.9fr', gap: 8, alignItems: 'center', padding: isMobile ? '6px 2px' : '8px 4px', borderTop: index === 0 ? '1px solid #EEF2F7' : '1px solid #F1F5F9' }}>
                                        <div style={{ fontSize: isMobile ? '0.65rem' : '0.74rem', fontWeight: 700, color: '#475569', lineHeight: 1.3 }}>{konu.konu}</div>
                                        <div style={{ textAlign: 'center', fontSize: isMobile ? '0.6rem' : '0.7rem', fontWeight: 800, color: '#94A3B8' }}>
                                          {konu.soru} / <span style={{ color: '#10B981' }}>{konu.dogru}</span> / <span style={{ color: '#F43F5E' }}>{konu.yanlis}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                                          <div style={{ width: isMobile ? 50 : 72, height: 8, borderRadius: 999, background: arka, overflow: 'hidden', flexShrink: 0 }}>
                                            <div style={{ width: `${Math.max(0, Math.min(100, konu.basari || 0))}%`, height: '100%', borderRadius: 999, background: renk }} />
                                          </div>
                                          <div style={{ minWidth: 36, textAlign: 'right', color: renk, fontWeight: 900, fontSize: isMobile ? '0.6rem' : '0.7rem' }}>%{konu.basari}</div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        ))}
                        {topicSummary.length === 0 && (
                          <div style={{ background: '#FFFFFF', border: '1px solid #E5EAF2', borderRadius: 14, padding: '12px 14px', color: '#64748B', fontSize: '0.82rem' }}>
                            Konu/kazanım kırılımı henüz üretilemedi. Bu görünüm için backend tarafında öğrenciye ait ders cevap dizileri ve/veya `konuAnaliz` verisi dönmelidir.
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ background: '#EEF4FF', border: '1px solid #D6E2FF', borderRadius: 16, padding: isMobile ? '10px 12px' : '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
                      <div style={{ width: isMobile ? 36 : 40, height: isMobile ? 36 : 40, borderRadius: 12, background: 'white', border: '1px solid #D9E5FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '0.9rem' : '1rem', flexShrink: 0 }}>🧠</div>
                      <div>
                        <div style={{ fontSize: isMobile ? '0.8rem' : '0.86rem', fontWeight: 900, color: '#2643A5' }}>Gelişim Önerisi</div>
                        <div style={{ marginTop: 6, fontSize: isMobile ? '0.7rem' : '0.78rem', lineHeight: 1.5, color: '#2952D1', maxWidth: 860 }}>{gelisimMetni}</div>
                      </div>
                    </div>
                  </div>
            
                  <div className="no-print" style={{ padding: isMobile ? '16px 20px' : '20px 40px', background: '#F1F5F9', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
                    <button className="btn btn-outline" onClick={() => setSeciliKarne(null)} style={{ padding: isMobile ? '12px' : '10px 20px' }}>Kapat</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* GİZLİ A4 PDF ALANI (Bu alan ekranda görünmez, sadece matbaa pdf çıktısı içindir) */}
      <div className="absolute top-[-9999px] left-[-9999px] pointer-events-none" id="karne-print-zone">
        {aktifPrintOgrenci && (
          <BireyselDetayliKarne
            student={aktifPrintOgrenci}
            sinavDetay={seciliSinavDetay}
            sinavOrtalamalari={hesaplaSinavOrtalamalari(sonuclar)}
            tumSonuclar={sonuclar}
          />
        )}
      </div>
    </div>
  );
}
